import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, Package, History, Settings, Plus, MapPin, Calendar,
  Filter, Search, MoreVertical, Eye, Edit, Trash2, Send, X, Clock,
  CheckCircle, XCircle, AlertCircle, AlertTriangle, Loader2, Navigation,
  FileSearch, DollarSign, CreditCard, FileText, TrendingUp, TrendingDown,
  Banknote, Wallet, Download, RefreshCw, Truck, User, Building, Phone, Mail, Users, Car
} from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/use-toast';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useClientStats } from '@/hooks/useClientStats';
import { useClientRequests } from '@/hooks/useClientRequests';
import { useClientOffers } from '@/hooks/useClientOffers';
import { useClientBookings } from '@/hooks/useClientBookings';
import { useClientPayments } from '@/hooks/useClientPayments';
import { clientAPI, api, vehiclesAPI } from '@/services/api';
import { getClientSidebarLinks } from '@/config/clientSidebarLinks';
import useConstants, { constantsUtils } from '@/hooks/useConstants';
import RouteVisualization from '@/components/transporter/RouteVisualization';

const ClientOperations = () => {
  const { stats } = useClientStats();
  const { constants } = useConstants();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Gestion de l'onglet actif via les paramètres d'URL
  const activeTab = searchParams.get('tab') || 'requests';

  // Gestion du sous-onglet pour les réservations
  const [bookingSubTab, setBookingSubTab] = useState(() => {
    const subTab = searchParams.get('bookingSubTab');
    return subTab === 'history' ? 'history' : 'active';
  });

  // Gestion des statistiques pour les badges
  const [badgeStats, setBadgeStats] = useState({
    requests: 0,
    offers: 0,
    bookings: 0,
    payments: 0,
    invoices: 0,
  });

  const sidebarLinks = getClientSidebarLinks();

  // État pour l'onglet Demandes
  const [requestFilters, setRequestFilters] = useState({
    status: 'all',
    search: '',
    urgency: 'all',
  });
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [requestData, setRequestData] = useState({
    from_location_id: null,
    to_location_id: null,
    from_location_name: '',
    to_location_name: '',
    preferred_pickup_date: '',
    preferred_delivery_date: '',
    declared_value: '',
    payment_terms: 'PREPAID',
    urgency_level: 'NORMAL',
    description: '',
    // Nouveaux champs métier
    handling_responsibility: '',
    goods_type: '',
    preferred_vehicle_type: null,
    estimated_weight_kg: '',
    estimated_volume_m3: '',
  });
  const [vehicleTypes, setVehicleTypes] = useState([]);
  const [loadingVehicleTypes, setLoadingVehicleTypes] = useState(false);

  // État pour l'onglet Offres
  const [offerFilters, setOfferFilters] = useState({
    status: 'all',
    search: '',
    request_id: null,
  });
  const [offerDetailDialogOpen, setOfferDetailDialogOpen] = useState(false);
  const [acceptDialogOpen, setAcceptDialogOpen] = useState(false);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);

  // État pour l'onglet Suivi
  const [trackingData, setTrackingData] = useState({});
  const [trackingLoading, setTrackingLoading] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);

  // État pour l'onglet Réservations
  const [bookingFilters, setBookingFilters] = useState({
    status_code: 'all',
    payment_status: 'all',
    search: '',
  });

  // États pour les modals de paiements
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [proofDialogOpen, setProofDialogOpen] = useState(false);
  const [selectedProof, setSelectedProof] = useState(null);

  // États pour la modale de création de paiement
  const [createPaymentDialogOpen, setCreatePaymentDialogOpen] = useState(false);
  const [paymentBooking, setPaymentBooking] = useState(null);
  const [paymentData, setPaymentData] = useState({
    method_code: '',
    amount: '',
    // Données spécifiques selon la méthode de paiement
    card_number: '',
    card_expiry: '',
    card_cvv: '',
    card_holder_name: '',
    phone_number: '',
    account_number: '',
    bank_name: '',
    reference: '',
    proof_file: null, // Pour CASH - preuve de paiement
  });

  // État pour l'onglet Finances
  const [financeFilters, setFinanceFilters] = useState({
    status: 'all',
    method: 'all',
    search: '',
  });
  const [financeActiveTab, setFinanceActiveTab] = useState('payments');

  // État pour l'onglet Historique
  const [historyFilters, setHistoryFilters] = useState({
    status: 'all',
    search: '',
    date_range: 'all',
  });
  const [historyActiveTab, setHistoryActiveTab] = useState('requests');

  // Hooks pour les données (seront utilisés dans les onglets)
  // Utiliser un seul appel pour toutes les demandes, puis filtrer côté client
  const { requests: allRequests, loading: requestsLoading, error: requestsError, createRequest, updateRequest, deleteRequest, publishRequest, unpublishRequest, cancelRequest } = useClientRequests({ status: 'all' });

  // Filtrer les demandes selon les besoins
  const requests = useMemo(() => {
    let filtered = allRequests;
    if (requestFilters.status !== 'all') {
      filtered = filtered.filter(r => r.status === requestFilters.status);
    }
    if (requestFilters.search) {
      const searchLower = requestFilters.search.toLowerCase();
      filtered = filtered.filter(r =>
        r.id.toLowerCase().includes(searchLower) ||
        (r.from_location?.city || r.from_location?.label || '')?.toLowerCase().includes(searchLower) ||
        (r.to_location?.city || r.to_location?.label || '')?.toLowerCase().includes(searchLower)
      );
    }
    if (requestFilters.urgency !== 'all') {
      filtered = filtered.filter(r => r.urgency_level === requestFilters.urgency);
    }
    return filtered;
  }, [allRequests, requestFilters]);

  const publishedRequests = useMemo(() =>
    allRequests.filter(r => r.status === 'PUBLISHED'),
    [allRequests]
  );

  const activeRequests = useMemo(() =>
    allRequests.filter(r => r.status === 'IN_TRANSIT' || r.status === 'ASSIGNED'),
    [allRequests]
  );

  const { offers, loading: offersLoading, error: offersError, acceptOffer, rejectOffer } = useClientOffers(
    offerFilters.request_id,
    { status: offerFilters.status === 'all' ? undefined : offerFilters.status, search: offerFilters.search }
  );
  const { bookings, loading: bookingsLoading, error: bookingsError } = useClientBookings(bookingFilters);
  const { payments, invoices, loading: paymentsLoading, error: paymentsError, initiatePayment } = useClientPayments(financeFilters);

  // Fonction pour compter les offres visibles pour une demande
  const getVisibleOffersCount = (requestId) => {
    return offers.filter(o =>
      o.request === requestId || o.request_detail?.id === requestId || o.request?.id === requestId
    ).filter(o => o.visible_to_client === true).length;
  };

  // Calculer les statistiques pour les badges
  useEffect(() => {
    const pendingRequests = allRequests.filter(r =>
      r.status === 'DRAFT' || r.status === 'PUBLISHED' || r.status === 'ASSIGNED'
    ).length;

    const pendingOffers = offers.filter(o =>
      o.status === 'PENDING' && o.visible_to_client === true
    ).length;

    const activeBookings = bookings.filter(b =>
      b.status_code === 'PENDING' || b.status_code === 'CONFIRMED' || b.status_code === 'IN_PROGRESS'
    ).length;

    const pendingPayments = payments.filter(p =>
      p.status === 'PENDING'
    ).length;

    const pendingInvoices = invoices.filter(i =>
      i.status === 'PENDING' || i.status === 'UNPAID'
    ).length;

    setBadgeStats({
      requests: pendingRequests,
      offers: pendingOffers,
      bookings: activeBookings,
      payments: pendingPayments,
      invoices: pendingInvoices,
    });
  }, [allRequests, offers, bookings, payments, invoices]);

  // Fonction pour grouper les locations par ville (éviter les doublons)
  const groupLocationsByCity = (locations) => {
    const cityMap = new Map();

    locations.forEach((location) => {
      const cityName = (location.city || location.label || '').toLowerCase().trim();

      if (cityName) {
        // Si la ville n'existe pas encore dans la map, l'ajouter
        if (!cityMap.has(cityName)) {
          cityMap.set(cityName, location);
        }
        // Sinon, garder le premier enregistrement trouvé (ou le plus récent si besoin)
        // On peut aussi choisir de garder celui avec le label le plus complet
        else {
          const existing = cityMap.get(cityName);
          // Garder celui qui a le label le plus complet
          const existingLabel = (existing.label || existing.city || '').trim();
          const newLabel = (location.label || location.city || '').trim();
          if (newLabel.length > existingLabel.length) {
            cityMap.set(cityName, location);
          }
        }
      }
    });

    return Array.from(cityMap.values());
  };

  // Charger les locations disponibles
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoadingLocations(true);
        const response = await clientAPI.getLocations({});
        const allLocations = response.data.results || response.data || [];
        // Grouper les locations par ville pour éviter les doublons
        const groupedLocations = groupLocationsByCity(allLocations);
        setLocations(groupedLocations);
      } catch (err) {
        console.error('Erreur lors du chargement des locations:', err);
      } finally {
        setLoadingLocations(false);
      }
    };
    fetchLocations();
  }, []);

  // Charger les types de véhicules disponibles
  useEffect(() => {
    const fetchVehicleTypes = async () => {
      try {
        setLoadingVehicleTypes(true);
        const response = await vehiclesAPI.getVehicleTypes();
        setVehicleTypes(response.data.results || response.data || []);
      } catch (err) {
        console.error('Erreur lors du chargement des types de véhicules:', err);
      } finally {
        setLoadingVehicleTypes(false);
      }
    };
    fetchVehicleTypes();
  }, []);

  // Gestion du changement d'onglet
  const handleTabChange = useCallback((value) => {
    const params = { tab: value };
    if (value === 'bookings') {
      params.bookingSubTab = bookingSubTab;
    }
    setSearchParams(params, { replace: true });
    // Si on change vers l'onglet requests, ouvrir le dialog de création si le bouton a été cliqué
    if (value === 'requests' && searchParams.get('create') === 'true') {
      setCreateDialogOpen(true);
      setSearchParams({ tab: 'requests' }, { replace: true });
    }
  }, [setSearchParams, searchParams, bookingSubTab]);

  // Mettre à jour l'URL quand le sous-onglet des réservations change
  useEffect(() => {
    if (activeTab === 'bookings') {
      setSearchParams({ tab: activeTab, bookingSubTab }, { replace: true });
    }
  }, [activeTab, bookingSubTab, setSearchParams]);

  // Fonctions utilitaires pour les badges
  const getStatusBadge = (status) => {
    const statusConfig = {
      'DRAFT': { label: 'Brouillon', color: 'bg-gray-500' },
      'PUBLISHED': { label: 'Publiée', color: 'bg-blue-500' },
      'ASSIGNED': { label: 'Assignée', color: 'bg-green-500' },
      'IN_TRANSIT': { label: 'En transit', color: 'bg-purple-500' },
      'COMPLETED': { label: 'Terminée', color: 'bg-green-600' },
      'CANCELLED': { label: 'Annulée', color: 'bg-red-500' },
    };
    const config = statusConfig[status] || { label: status, color: 'bg-gray-500' };
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  const getUrgencyBadge = (urgency) => {
    const urgencyConfig = {
      'NORMAL': { label: 'Normal', color: 'bg-blue-500' },
      'URGENT': { label: 'Urgent', color: 'bg-red-500' },
    };
    const config = urgencyConfig[urgency] || { label: urgency, color: 'bg-gray-500' };
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  const getPaymentTermsBadge = (terms) => {
    const termsConfig = {
      'PREPAID': { label: 'Prépaiement', color: 'bg-green-500' },
      'COD': { label: 'Paiement à la livraison', color: 'bg-yellow-500' },
      'POSTPAID': { label: 'Paiement différé', color: 'bg-blue-500' },
    };
    const config = termsConfig[terms] || { label: terms, color: 'bg-gray-500' };
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    // Formater avec séparateur de milliers (espace) et couleur secondaire
    const formatted = new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
    return `${formatted} GNF`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Fonction pour obtenir le badge de statut de paiement pour un paiement individuel
  const getPaymentStatusBadgeForPayment = (status) => {
    if (!constants) return <Badge className="bg-gray-500 text-white">{status}</Badge>;
    const label = constantsUtils.getLabel(constants.payment_status, status);
    const statusConfig = {
      'PENDING': { color: 'bg-yellow-500' },
      'SUCCESS': { color: 'bg-green-500' },
      'FAILED': { color: 'bg-red-500' },
      'REFUNDED': { color: 'bg-orange-500' },
    };
    const config = statusConfig[status] || { color: 'bg-gray-500' };
    return (
      <Badge className={`${config.color} text-white`}>
        {label}
      </Badge>
    );
  };

  // Fonction pour obtenir le badge de statut de paiement pour un booking
  const getPaymentStatusBadge = (booking) => {
    const paymentPercentage = booking.payment_percentage || 0;
    const totalPaid = booking.total_paid || 0;
    const agreedPrice = booking.agreed_price || 0;

    if (paymentPercentage >= 100) {
      return (
        <Badge className="bg-green-500 text-white flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          Payé
        </Badge>
      );
    } else if (paymentPercentage > 0) {
      return (
        <Badge className="bg-yellow-500 text-white flex items-center gap-1">
          <Clock className="w-3 h-3" />
          Partiel ({paymentPercentage}%)
        </Badge>
      );
    } else {
      return (
        <Badge className="bg-red-500 text-white flex items-center gap-1">
          <XCircle className="w-3 h-3" />
          Non payé
        </Badge>
      );
    }
  };

  // Fonction pour gérer l'affichage des paiements
  const handleViewPayments = async (bookingId) => {
    try {
      const { clientAPI } = await import('@/services/api');
      const response = await clientAPI.getBookingById(bookingId);
      setSelectedBooking(response.data);
      setPaymentDialogOpen(true);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les paiements',
        variant: 'destructive',
      });
    }
  };

  // Fonction pour ouvrir la modale de création de paiement
  const handlePayBooking = async (booking) => {
    try {
      // Calculer le montant restant à payer
      const totalPaid = booking.total_paid || 0;
      const agreedPrice = booking.agreed_price || booking.price || 0;
      const remainingAmount = Math.max(0, agreedPrice - totalPaid);

      if (remainingAmount <= 0) {
        toast({
          title: 'Info',
          description: 'Cette réservation est déjà entièrement payée.',
        });
        return;
      }

      // Charger les détails complets du booking
      const { clientAPI } = await import('@/services/api');
      const response = await clientAPI.getBookingById(booking.id);
      setPaymentBooking(response.data);
      setPaymentData({
        method_code: '',
        amount: remainingAmount.toString(),
        card_number: '',
        card_expiry: '',
        card_cvv: '',
        card_holder_name: '',
        phone_number: '',
        account_number: '',
        bank_name: '',
        reference: '',
        proof_file: null,
      });
      setCreatePaymentDialogOpen(true);
    } catch (err) {
      toast({
        title: 'Erreur',
        description: 'Impossible d\'initialiser le paiement',
        variant: 'destructive',
      });
    }
  };

  // Fonction pour valider les données selon la méthode de paiement
  const validatePaymentData = () => {
    if (!paymentData.method_code || !paymentData.amount) {
      return { valid: false, message: 'Veuillez sélectionner une méthode de paiement et entrer un montant.' };
    }

    switch (paymentData.method_code) {
      case 'CARD':
        if (!paymentData.card_number || !paymentData.card_expiry || !paymentData.card_cvv || !paymentData.card_holder_name) {
          return { valid: false, message: 'Veuillez remplir tous les champs de la carte bancaire.' };
        }
        if (paymentData.card_number.replace(/\s/g, '').length < 16) {
          return { valid: false, message: 'Le numéro de carte doit contenir 16 chiffres.' };
        }
        if (paymentData.card_cvv.length < 3) {
          return { valid: false, message: 'Le CVV doit contenir 3 chiffres.' };
        }
        break;
      case 'ORANGE_MONEY':
      case 'MTN_MOMO':
        if (!paymentData.phone_number || paymentData.phone_number.length < 9) {
          return { valid: false, message: 'Veuillez entrer un numéro de téléphone valide (9 chiffres).' };
        }
        break;
      case 'BANK_TRANSFER':
        if (!paymentData.bank_name || !paymentData.account_number || !paymentData.reference) {
          return { valid: false, message: 'Veuillez remplir tous les champs du virement bancaire.' };
        }
        break;
      case 'CASH':
        if (!paymentData.proof_file) {
          return { valid: false, message: 'Veuillez télécharger une preuve de paiement pour le paiement en espèces.' };
        }
        break;
      default:
        return { valid: false, message: 'Méthode de paiement non reconnue.' };
    }

    return { valid: true };
  };

  // Fonction pour créer un paiement
  const handleCreatePayment = async () => {
    if (!paymentBooking) {
      toast({
        title: 'Erreur',
        description: 'Aucune réservation sélectionnée.',
        variant: 'destructive',
      });
      return;
    }

    // Valider les données selon la méthode
    const validation = validatePaymentData();
    if (!validation.valid) {
      toast({
        title: 'Erreur',
        description: validation.message,
        variant: 'destructive',
      });
      return;
    }

    try {
      const { clientAPI } = await import('@/services/api');

      // Récupérer le transporteur depuis le booking
      const transporter = paymentBooking.transporter_detail || paymentBooking.transporter;
      if (!transporter) {
        toast({
          title: 'Erreur',
          description: 'Impossible de déterminer le transporteur.',
          variant: 'destructive',
        });
        return;
      }

      // Le transporteur a un champ user qui est l'ID de l'utilisateur (ForeignKey sérialisé comme ID)
      const payeeUserId = transporter.user;

      if (!payeeUserId) {
        toast({
          title: 'Erreur',
          description: 'Impossible de déterminer l\'utilisateur du transporteur.',
          variant: 'destructive',
        });
        return;
      }

      // Créer le paiement
      // Note: payer_user sera automatiquement défini par le backend (l'utilisateur actuel via perform_create)
      const paymentPayload = {
        booking: paymentBooking.id,
        payee_user: payeeUserId, // Le transporteur qui reçoit le paiement (ID utilisateur)
        amount: parseFloat(paymentData.amount),
        currency: 'GNF',
        method_code: paymentData.method_code,
        status: 'PENDING',
      };

      const response = await api.post('/payments/payments/', paymentPayload);

      toast({
        title: 'Paiement créé',
        description: 'Le paiement a été créé avec succès. Vous pouvez maintenant l\'initier.',
      });

      // Fermer la modale et réinitialiser les données
      setCreatePaymentDialogOpen(false);
      setPaymentBooking(null);
      setPaymentData({
        method_code: '',
        amount: '',
        card_number: '',
        card_expiry: '',
        card_cvv: '',
        card_holder_name: '',
        phone_number: '',
        account_number: '',
        bank_name: '',
        reference: '',
        proof_file: null,
      });

      // Recharger les bookings pour mettre à jour les paiements
      // Note: fetchBookings n'est pas disponible directement, on utilise le hook useClientBookings
      // Les bookings seront automatiquement rafraîchis par le hook

      // Optionnel : initier automatiquement le paiement
      if (response.data.id) {
        try {
          await clientAPI.initiatePayment(response.data.id);
          toast({
            title: 'Paiement initié',
            description: 'Le processus de paiement a été lancé. Suivez les instructions.',
          });
        } catch (initError) {
          // Si l'initiation échoue, le paiement est quand même créé
          console.error('Erreur lors de l\'initiation du paiement:', initError);
        }
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.response?.data?.message || 'Erreur lors de la création du paiement.';
      toast({
        title: 'Erreur',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  // Fonction pour afficher la preuve de paiement
  const handleViewProof = (proofUrl) => {
    setSelectedProof(proofUrl);
    setProofDialogOpen(true);
  };

  // Fonction pour télécharger la preuve de paiement
  const handleDownloadProof = (proofUrl, paymentId) => {
    const link = document.createElement('a');
    link.href = proofUrl;
    link.download = `preuve_paiement_${paymentId.slice(-8)}.${proofUrl.split('.').pop()}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Handlers pour les demandes
  const handleCreateRequest = async () => {
    const hasFromLocation = requestData.from_location_id && requestData.from_location_id !== 'new';
    const hasFromLocationName = requestData.from_location_name && requestData.from_location_name.trim() !== '';
    const hasToLocation = requestData.to_location_id && requestData.to_location_id !== 'new';
    const hasToLocationName = requestData.to_location_name && requestData.to_location_name.trim() !== '';

    if ((!hasFromLocation && !hasFromLocationName) || (!hasToLocation && !hasToLocationName)) {
      toast({
        title: 'Erreur',
        description: 'Veuillez sélectionner ou saisir les lieux de départ et d\'arrivée.',
        variant: 'destructive',
      });
      return;
    }

    let fromLocationId = hasFromLocation ? requestData.from_location_id : null;
    let toLocationId = hasToLocation ? requestData.to_location_id : null;

    if (hasFromLocationName && !fromLocationId) {
      try {
        const response = await clientAPI.createLocation({
          city: requestData.from_location_name,
          country: 'Guinée',
        });
        fromLocationId = response.data.id;
      } catch (err) {
        // Vérifier si c'est une erreur de doublon
        if (err.response?.data?.existing_location) {
          const existingLocation = err.response.data.existing_location;
          toast({
            title: 'Ville existante détectée',
            description: `La ville "${existingLocation.city}" existe déjà. Veuillez la sélectionner dans la liste déroulante.`,
            variant: 'default',
          });
          // Recharger les locations pour inclure celle qui existe déjà
          await fetchLocations();
          // Suggérer à l'utilisateur de sélectionner la ville existante
          setRequestData(prev => ({
            ...prev,
            from_location_id: existingLocation.id,
            from_location_name: '',
          }));
          return;
        }

        // Fallback : chercher une location existante
        try {
          const locationsResponse = await clientAPI.getLocations({ city: requestData.from_location_name });
          const fetchedLocations = locationsResponse.data.results || locationsResponse.data || [];
          const existing = fetchedLocations.find(l =>
            l.city?.toLowerCase() === requestData.from_location_name.toLowerCase()
          );
          if (existing) {
            fromLocationId = existing.id;
          } else {
            throw new Error('Location non trouvée');
          }
        } catch (searchErr) {
          toast({
            title: 'Erreur',
            description: 'Impossible de créer ou trouver le lieu de départ. Veuillez vérifier le nom de la ville.',
            variant: 'destructive',
          });
          return;
        }
      }
    }

    if (hasToLocationName && !toLocationId) {
      try {
        const response = await clientAPI.createLocation({
          city: requestData.to_location_name,
          country: 'Guinée',
        });
        toLocationId = response.data.id;
      } catch (err) {
        // Vérifier si c'est une erreur de doublon
        if (err.response?.data?.existing_location) {
          const existingLocation = err.response.data.existing_location;
          toast({
            title: 'Ville existante détectée',
            description: `La ville "${existingLocation.city}" existe déjà. Veuillez la sélectionner dans la liste déroulante.`,
            variant: 'default',
          });
          // Recharger les locations pour inclure celle qui existe déjà
          await fetchLocations();
          // Suggérer à l'utilisateur de sélectionner la ville existante
          setRequestData(prev => ({
            ...prev,
            to_location_id: existingLocation.id,
            to_location_name: '',
          }));
          return;
        }

        // Fallback : chercher une location existante
        try {
          const locationsResponse = await clientAPI.getLocations({ city: requestData.to_location_name });
          const fetchedLocations = locationsResponse.data.results || locationsResponse.data || [];
          const existing = fetchedLocations.find(l =>
            l.city?.toLowerCase() === requestData.to_location_name.toLowerCase()
          );
          if (existing) {
            toLocationId = existing.id;
          } else {
            throw new Error('Location non trouvée');
          }
        } catch (searchErr) {
          toast({
            title: 'Erreur',
            description: 'Impossible de créer ou trouver le lieu d\'arrivée. Veuillez vérifier le nom de la ville.',
            variant: 'destructive',
          });
          return;
        }
      }
    }

    if (!fromLocationId || !toLocationId) {
      toast({
        title: 'Erreur',
        description: 'Les lieux de départ et d\'arrivée sont requis.',
        variant: 'destructive',
      });
      return;
    }

    if (fromLocationId === toLocationId) {
      toast({
        title: 'Erreur',
        description: 'Le lieu de départ et d\'arrivée doivent être différents.',
        variant: 'destructive',
      });
      return;
    }

    const result = await createRequest({
      from_location: fromLocationId,
      to_location: toLocationId,
      preferred_pickup_date: requestData.preferred_pickup_date || null,
      preferred_delivery_date: requestData.preferred_delivery_date || null,
      declared_value: requestData.declared_value ? parseFloat(requestData.declared_value) : null,
      payment_terms: requestData.payment_terms || 'PREPAID',
      urgency_level: requestData.urgency_level || 'NORMAL',
      description: requestData.description || '',
      // Nouveaux champs métier
      handling_responsibility: requestData.handling_responsibility || null,
      goods_type: requestData.goods_type && requestData.goods_type !== 'none' ? requestData.goods_type : null,
      preferred_vehicle_type_code: requestData.preferred_vehicle_type && requestData.preferred_vehicle_type !== 'none' ? requestData.preferred_vehicle_type : null,
      estimated_weight_kg: requestData.estimated_weight_kg ? parseFloat(requestData.estimated_weight_kg) : null,
      estimated_volume_m3: requestData.estimated_volume_m3 ? parseFloat(requestData.estimated_volume_m3) : null,
    });

    if (result.success) {
      toast({
        title: 'Demande créée',
        description: 'Votre demande de transport a été créée avec succès.',
      });
      setCreateDialogOpen(false);
      setRequestData({
        from_location_id: null,
        to_location_id: null,
        from_location_name: '',
        to_location_name: '',
        preferred_pickup_date: '',
        preferred_delivery_date: '',
        declared_value: '',
        payment_terms: 'PREPAID',
        urgency_level: 'NORMAL',
        description: '',
        // Réinitialiser les nouveaux champs métier
        handling_responsibility: '',
        goods_type: '',
        preferred_vehicle_type: null,
        estimated_weight_kg: '',
        estimated_volume_m3: '',
      });
    } else {
      let errorMessage = 'Erreur lors de la création de la demande.';
      if (typeof result.error === 'string') {
        errorMessage = result.error;
      } else if (result.error && typeof result.error === 'object') {
        if (result.error.detail) {
          errorMessage = result.error.detail;
        } else if (result.error.message) {
          errorMessage = result.error.message;
        } else if (Array.isArray(result.error)) {
          errorMessage = result.error.join(', ');
        }
      }
      toast({
        title: 'Erreur',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleUpdateRequest = async () => {
    if (!selectedRequest) return;

    // Gérer les localisations comme dans handleCreateRequest
    let fromLocationId = requestData.from_location_id && requestData.from_location_id !== 'new' ? requestData.from_location_id : null;
    let toLocationId = requestData.to_location_id && requestData.to_location_id !== 'new' ? requestData.to_location_id : null;

    if (requestData.from_location_name && !fromLocationId) {
      try {
        const response = await clientAPI.createLocation({
          city: requestData.from_location_name,
          country: 'Guinée',
        });
        fromLocationId = response.data.id;
      } catch (err) {
        // Vérifier si c'est une erreur de doublon
        if (err.response?.data?.existing_location) {
          const existingLocation = err.response.data.existing_location;
          toast({
            title: 'Ville existante détectée',
            description: `La ville "${existingLocation.city}" existe déjà. Veuillez la sélectionner dans la liste déroulante.`,
            variant: 'default',
          });
          // Recharger les locations pour inclure celle qui existe déjà
          await fetchLocations();
          // Suggérer à l'utilisateur de sélectionner la ville existante
          setRequestData(prev => ({
            ...prev,
            from_location_id: existingLocation.id,
            from_location_name: '',
          }));
          return;
        }

        // Fallback : chercher une location existante
        try {
          const locationsResponse = await clientAPI.getLocations({ city: requestData.from_location_name });
          const fetchedLocations = locationsResponse.data.results || locationsResponse.data || [];
          const existing = fetchedLocations.find(l =>
            (l.city || l.label)?.toLowerCase() === requestData.from_location_name.toLowerCase()
          );
          if (existing) {
            fromLocationId = existing.id;
          } else {
            throw new Error('Location non trouvée');
          }
        } catch (searchErr) {
          toast({
            title: 'Erreur',
            description: 'Impossible de créer ou trouver le lieu de départ. Veuillez vérifier le nom de la ville.',
            variant: 'destructive',
          });
          return;
        }
      }
    }

    if (requestData.to_location_name && !toLocationId) {
      try {
        const response = await clientAPI.createLocation({
          city: requestData.to_location_name,
          country: 'Guinée',
        });
        toLocationId = response.data.id;
      } catch (err) {
        // Vérifier si c'est une erreur de doublon
        if (err.response?.data?.existing_location) {
          const existingLocation = err.response.data.existing_location;
          toast({
            title: 'Ville existante détectée',
            description: `La ville "${existingLocation.city}" existe déjà. Veuillez la sélectionner dans la liste déroulante.`,
            variant: 'default',
          });
          // Recharger les locations pour inclure celle qui existe déjà
          await fetchLocations();
          // Suggérer à l'utilisateur de sélectionner la ville existante
          setRequestData(prev => ({
            ...prev,
            to_location_id: existingLocation.id,
            to_location_name: '',
          }));
          return;
        }

        // Fallback : chercher une location existante
        try {
          const locationsResponse = await clientAPI.getLocations({ city: requestData.to_location_name });
          const fetchedLocations = locationsResponse.data.results || locationsResponse.data || [];
          const existing = fetchedLocations.find(l =>
            (l.city || l.label)?.toLowerCase() === requestData.to_location_name.toLowerCase()
          );
          if (existing) {
            toLocationId = existing.id;
          } else {
            throw new Error('Location non trouvée');
          }
        } catch (searchErr) {
          toast({
            title: 'Erreur',
            description: 'Impossible de créer ou trouver le lieu d\'arrivée. Veuillez vérifier le nom de la ville.',
            variant: 'destructive',
          });
          return;
        }
      }
    }

    const updateData = {
      preferred_pickup_date: requestData.preferred_pickup_date || null,
      preferred_delivery_date: requestData.preferred_delivery_date || null,
      declared_value: requestData.declared_value ? parseFloat(requestData.declared_value) : null,
      payment_terms: requestData.payment_terms,
      urgency_level: requestData.urgency_level,
      description: requestData.description || '',
      // Nouveaux champs métier
      handling_responsibility: requestData.handling_responsibility || null,
      goods_type: requestData.goods_type && requestData.goods_type !== 'none' ? requestData.goods_type : null,
      preferred_vehicle_type_code: requestData.preferred_vehicle_type && requestData.preferred_vehicle_type !== 'none' ? requestData.preferred_vehicle_type : null,
      estimated_weight_kg: requestData.estimated_weight_kg ? parseFloat(requestData.estimated_weight_kg) : null,
      estimated_volume_m3: requestData.estimated_volume_m3 ? parseFloat(requestData.estimated_volume_m3) : null,
    };

    // Si les localisations ont changé, les mettre à jour
    if (fromLocationId && fromLocationId !== selectedRequest.from_location?.id) {
      updateData.from_location = fromLocationId;
    }
    if (toLocationId && toLocationId !== selectedRequest.to_location?.id) {
      updateData.to_location = toLocationId;
    }

    const result = await updateRequest(selectedRequest.id, updateData);

    if (result.success) {
      toast({
        title: 'Demande mise à jour',
        description: 'Votre demande a été mise à jour avec succès.',
      });
      setEditDialogOpen(false);
      setSelectedRequest(null);
    } else {
      let errorMessage = 'Erreur lors de la mise à jour de la demande.';
      if (typeof result.error === 'string') {
        errorMessage = result.error;
      } else if (result.error && typeof result.error === 'object') {
        if (result.error.detail) {
          errorMessage = result.error.detail;
        } else if (result.error.message) {
          errorMessage = result.error.message;
        }
      }
      toast({
        title: 'Erreur',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleDeleteRequest = async () => {
    if (!selectedRequest) return;

    const result = await deleteRequest(selectedRequest.id);
    if (result.success) {
      toast({
        title: 'Demande supprimée',
        description: 'Votre demande a été supprimée avec succès.',
      });
      setDeleteDialogOpen(false);
      setSelectedRequest(null);
    } else {
      let errorMessage = 'Erreur lors de la suppression de la demande.';
      if (typeof result.error === 'string') {
        errorMessage = result.error;
      } else if (result.error && typeof result.error === 'object') {
        if (result.error.detail) {
          errorMessage = result.error.detail;
        } else if (result.error.message) {
          errorMessage = result.error.message;
        }
      }
      toast({
        title: 'Erreur',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handlePublishRequest = async (requestId) => {
    const result = await publishRequest(requestId);
    if (result.success) {
      toast({
        title: 'Demande publiée',
        description: 'Votre demande a été publiée et est maintenant visible par les transporteurs.',
      });
    } else {
      let errorMessage = 'Erreur lors de la publication de la demande.';
      if (typeof result.error === 'string') {
        errorMessage = result.error;
      } else if (result.error && typeof result.error === 'object') {
        if (result.error.detail) {
          errorMessage = result.error.detail;
        } else if (result.error.message) {
          errorMessage = result.error.message;
        }
      }
      toast({
        title: 'Erreur',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleCancelRequest = async (requestId) => {
    const result = await cancelRequest(requestId);
    if (result.success) {
      toast({
        title: 'Demande annulée',
        description: 'Votre demande a été annulée avec succès.',
      });
    } else {
      let errorMessage = 'Erreur lors de l\'annulation de la demande.';
      if (typeof result.error === 'string') {
        errorMessage = result.error;
      } else if (result.error && typeof result.error === 'object') {
        if (result.error.detail) {
          errorMessage = result.error.detail;
        } else if (result.error.message) {
          errorMessage = result.error.message;
        }
      }
      toast({
        title: 'Erreur',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleEditRequest = (request) => {
    setSelectedRequest(request);
    setRequestData(prev => ({
      ...prev,
      from_location_id: request.from_location?.id,
      to_location_id: request.to_location?.id,
      preferred_pickup_date: request.preferred_pickup_date || '',
      preferred_delivery_date: request.preferred_delivery_date || '',
      declared_value: request.declared_value || '',
      payment_terms: request.payment_terms || 'PREPAID',
      urgency_level: request.urgency_level || 'NORMAL',
      description: request.description || '',
      // Nouveaux champs métier
      handling_responsibility: request.handling_responsibility || '',
      goods_type: request.goods_type || '',
      preferred_vehicle_type: request.preferred_vehicle_type || request.preferred_vehicle_type_detail?.code || null,
      estimated_weight_kg: request.estimated_weight_kg || '',
      estimated_volume_m3: request.estimated_volume_m3 || '',
    }));
    setEditDialogOpen(true);
  };

  // Fonctions utilitaires pour les offres
  const getOfferStatusBadge = (status) => {
    const statusConfig = {
      'PENDING': { label: 'En attente', color: 'bg-yellow-500', icon: Clock },
      'ACCEPTED': { label: 'Acceptée', color: 'bg-green-500', icon: CheckCircle },
      'REJECTED': { label: 'Rejetée', color: 'bg-red-500', icon: XCircle },
      'EXPIRED': { label: 'Expirée', color: 'bg-gray-500', icon: AlertCircle },
    };
    const config = statusConfig[status] || { label: status, color: 'bg-gray-500', icon: AlertCircle };
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {config.label}
      </Badge>
    );
  };

  // Regrouper les offres par demande
  const offersByRequest = useMemo(() => {
    const grouped = {};
    offers.forEach(offer => {
      const requestId = offer.request?.id;
      if (!grouped[requestId]) {
        grouped[requestId] = [];
      }
      grouped[requestId].push(offer);
    });
    return grouped;
  }, [offers]);

  const getRequestOffers = () => {
    if (!offerFilters.request_id || offerFilters.request_id === 'all') {
      return Object.entries(offersByRequest).flatMap(([requestId, requestOffers]) =>
        requestOffers.map(offer => ({ ...offer, requestId }))
      );
    }
    return offersByRequest[offerFilters.request_id] || [];
  };

  const displayOffers = getRequestOffers();

  // Handlers pour les offres
  const handleAcceptOffer = async (offer) => {
    setSelectedOffer(offer);
    setAcceptDialogOpen(true);
  };

  const confirmAcceptOffer = async () => {
    if (!selectedOffer) return;
    const result = await acceptOffer(selectedOffer.id);
    if (result.success) {
      toast({
        title: 'Offre acceptée',
        description: 'Vous avez accepté l\'offre. Un paiement peut être requis.',
      });
      setAcceptDialogOpen(false);
      setSelectedOffer(null);
    } else {
      toast({
        title: 'Erreur',
        description: result.error || 'Erreur lors de l\'acceptation de l\'offre.',
        variant: 'destructive',
      });
    }
  };

  const handleRejectOffer = async (offer) => {
    setSelectedOffer(offer);
    setRejectDialogOpen(true);
  };

  const confirmRejectOffer = async () => {
    if (!selectedOffer) return;
    const result = await rejectOffer(selectedOffer.id);
    if (result.success) {
      toast({
        title: 'Offre rejetée',
        description: 'Vous avez rejeté l\'offre.',
      });
      setRejectDialogOpen(false);
      setSelectedOffer(null);
    } else {
      toast({
        title: 'Erreur',
        description: result.error || 'Erreur lors du rejet de l\'offre.',
        variant: 'destructive',
      });
    }
  };

  // Fonctions pour le suivi
  const getTrackingStatusBadge = (status) => {
    const statusConfig = {
      'STARTED': { label: 'Démarré', color: 'bg-blue-500' },
      'EN_ROUTE': { label: 'En route', color: 'bg-purple-500' },
      'AT_DELIVERY': { label: 'En livraison', color: 'bg-orange-500' },
      'DELIVERED': { label: 'Livré', color: 'bg-green-500' },
      'DELAYED': { label: 'Retardé', color: 'bg-red-500' },
    };
    const config = statusConfig[status] || { label: status, color: 'bg-gray-500' };
    return <Badge className={`${config.color} text-white`}>{config.label}</Badge>;
  };

  const fetchTrackingData = useCallback(async (requestId) => {
    try {
      setTrackingLoading(true);
      const response = await clientAPI.trackShipment(requestId);
      setTrackingData(prev => ({
        ...prev,
        [requestId]: response.data.results || response.data || [],
      }));
    } catch (err) {
      console.error('Erreur lors du chargement du suivi:', err);
      toast({
        title: 'Erreur',
        description: 'Erreur lors du chargement des données de suivi.',
        variant: 'destructive',
      });
    } finally {
      setTrackingLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedRequestId) {
      fetchTrackingData(selectedRequestId);
      const interval = setInterval(() => {
        fetchTrackingData(selectedRequestId);
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [selectedRequestId, fetchTrackingData]);

  const trackingEvents = selectedRequestId ? (trackingData[selectedRequestId] || []) : [];
  const filteredActiveRequests = activeRequests.filter(req =>
    req.status === 'IN_TRANSIT' || req.status === 'ASSIGNED'
  );

  // Fonctions utilitaires pour les réservations
  const getBookingStatusBadge = (status) => {
    if (!constants) {
      return <Badge className="bg-gray-500 text-white">{status}</Badge>;
    }

    const label = constantsUtils.getLabel(constants.booking_status, status);
    const statusConfig = {
      'CONFIRMED': { color: 'bg-blue-500', icon: CheckCircle },
      'IN_PROGRESS': { color: 'bg-green-500', icon: Navigation },
      'DELIVERED': { color: 'bg-purple-500', icon: Package },
      'COMPLETED': { color: 'bg-gray-500', icon: CheckCircle },
      'CANCELLED': { color: 'bg-red-500', icon: XCircle },
      'DISPUTED': { color: 'bg-orange-500', icon: AlertTriangle },
    };
    const config = statusConfig[status] || { color: 'bg-gray-500', icon: Clock };
    const Icon = config.icon;

    return (
      <Badge className={`${config.color} text-white flex items-center gap-1`}>
        <Icon className="w-3 h-3" />
        {label}
      </Badge>
    );
  };

  // Fonction pour obtenir le label des termes de paiement
  const getPaymentTermsLabel = (terms) => {
    if (!terms || !constants?.payment_terms) return terms || 'N/A';
    const paymentTerm = constants.payment_terms.find(term => term[0] === terms);
    return paymentTerm ? paymentTerm[1] : terms;
  };

  // Note: getPaymentStatusBadgeForPayment est déjà définie plus haut pour les paiements individuels

  const getPaymentMethodIcon = (method) => {
    const iconConfig = {
      'ORANGE_MONEY': CreditCard,
      'MTN_MOMO': CreditCard,
      'BANK_TRANSFER': Banknote,
      'CARD': CreditCard,
      'CASH': DollarSign,
    };
    const Icon = iconConfig[method] || CreditCard;
    return <Icon className="w-4 h-4" />;
  };

  // Handlers pour les paiements
  const handleInitiatePayment = async (paymentId) => {
    const result = await initiatePayment(paymentId);
    if (result.success) {
      toast({
        title: 'Paiement initié',
        description: 'Le processus de paiement a été lancé. Suivez les instructions.',
      });
    } else {
      toast({
        title: 'Erreur',
        description: result.error || 'Erreur lors de l\'initiation du paiement.',
        variant: 'destructive',
      });
    }
  };

  // Filtrer les demandes terminées/annulées pour l'historique
  const completedRequests = useMemo(() => {
    return allRequests.filter(req =>
      req.status === 'COMPLETED' || req.status === 'CANCELLED'
    );
  }, [allRequests]);

  const filteredHistoryRequests = useMemo(() => {
    let filtered = completedRequests;

    if (historyFilters.search) {
      filtered = filtered.filter(req => {
        const searchLower = historyFilters.search.toLowerCase();
        return (
          req.id.toLowerCase().includes(searchLower) ||
          (req.from_location?.city || req.from_location?.label || '')?.toLowerCase().includes(searchLower) ||
          (req.to_location?.city || req.to_location?.label || '')?.toLowerCase().includes(searchLower)
        );
      });
    }

    if (historyFilters.status !== 'all') {
      filtered = filtered.filter(req => req.status === historyFilters.status);
    }

    return filtered;
  }, [completedRequests, historyFilters]);

  // Statistiques pour les finances
  const totalPaid = useMemo(() => {
    return payments
      .filter(p => p.status === 'SUCCESS')
      .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  }, [payments]);

  const pendingAmount = useMemo(() => {
    return payments
      .filter(p => p.status === 'PENDING')
      .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  }, [payments]);

  return (
    <>
      <Helmet>
        <title>Mes opérations - LogiConnect</title>
        <meta name="description" content="Gérez vos demandes, offres, suivi et paiements" />
      </Helmet>
      <DashboardLayout sidebarLinks={sidebarLinks}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-white">Mes opérations</h1>
            <p className="text-muted-foreground mt-2">
              Gérez toutes vos demandes, offres, suivi et paiements en un seul endroit
            </p>
          </div>
          <Button
            className="gradient-secondary text-primary font-semibold w-full sm:w-auto"
            onClick={() => {
              handleTabChange('requests');
              setCreateDialogOpen(true);
            }}
          >
            <Plus className="w-5 h-5 mr-2" />
            Nouvelle demande
          </Button>
        </div>

        {/* Système d'onglets principal */}
        <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
          <TabsList className="grid w-full grid-cols-3 lg:grid-cols-5 mb-4">
            <TabsTrigger
              value="requests"
              className="flex items-center gap-2 relative data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground"
            >
              <Package className="w-4 h-4" />
              <span className="hidden sm:inline">Demandes</span>
              {badgeStats.requests > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5 text-[10px]">
                  {badgeStats.requests > 99 ? '99+' : badgeStats.requests}
                </Badge>
              )}
            </TabsTrigger>

            <TabsTrigger
              value="tracking"
              className="flex items-center gap-2 relative data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground"
            >
              <Navigation className="w-4 h-4" />
              <span className="hidden sm:inline">Suivi</span>
            </TabsTrigger>

            <TabsTrigger
              value="bookings"
              className="flex items-center gap-2 relative data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground"
            >
              <Calendar className="w-4 h-4" />
              <span className="hidden sm:inline">Réservations</span>
              {badgeStats.bookings > 0 && (
                <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5 text-[10px]">
                  {badgeStats.bookings > 99 ? '99+' : badgeStats.bookings}
                </Badge>
              )}
            </TabsTrigger>

            <TabsTrigger
              value="finances"
              className="flex items-center gap-2 relative data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground"
            >
              <DollarSign className="w-4 h-4" />
              <span className="hidden sm:inline">Finances</span>
              {(badgeStats.payments > 0 || badgeStats.invoices > 0) && (
                <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5 text-[10px]">
                  {(badgeStats.payments + badgeStats.invoices) > 99 ? '99+' : (badgeStats.payments + badgeStats.invoices)}
                </Badge>
              )}
            </TabsTrigger>

            <TabsTrigger
              value="history"
              className="flex items-center gap-2 relative data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground"
            >
              <History className="w-4 h-4" />
              <span className="hidden sm:inline">Historique</span>
            </TabsTrigger>
          </TabsList>

          {/* Contenu des onglets */}
          <TabsContent value="requests" className="space-y-4">
            {/* Filtres */}
            <Card className="bg-card/50 border-border">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                      <Input
                        placeholder="Rechercher une demande..."
                        value={requestFilters.search}
                        onChange={(e) => setRequestFilters(prev => ({ ...prev, search: e.target.value }))}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <Select value={requestFilters.status} onValueChange={(value) => setRequestFilters(prev => ({ ...prev, status: value }))}>
                    <SelectTrigger className="w-full sm:w-48">
                      <SelectValue placeholder="Statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="DRAFT">Brouillon</SelectItem>
                      <SelectItem value="PUBLISHED">Publiée</SelectItem>
                      <SelectItem value="ASSIGNED">Assignée</SelectItem>
                      <SelectItem value="IN_TRANSIT">En transit</SelectItem>
                      <SelectItem value="COMPLETED">Terminée</SelectItem>
                      <SelectItem value="CANCELLED">Annulée</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={requestFilters.urgency} onValueChange={(value) => setRequestFilters(prev => ({ ...prev, urgency: value }))}>
                    <SelectTrigger className="w-full sm:w-48">
                      <SelectValue placeholder="Urgence" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les urgences</SelectItem>
                      <SelectItem value="NORMAL">Normal</SelectItem>
                      <SelectItem value="URGENT">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Liste des demandes */}
            {requestsLoading ? (
              <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : requestsError ? (
              <Card className="bg-card/50 border-border">
                <CardContent className="p-12 text-center">
                  <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Erreur</h3>
                  <p className="text-muted-foreground">{requestsError}</p>
                </CardContent>
              </Card>
            ) : requests.length === 0 ? (
              <Card className="bg-card/50 border-border">
                <CardContent className="p-12 text-center">
                  <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Aucune demande</h3>
                  <p className="text-muted-foreground mb-4">Vous n'avez pas encore de demande de transport.</p>
                  <Button onClick={() => setCreateDialogOpen(true)}>
                    <Plus className="w-5 h-5 mr-2" />
                    Créer une demande
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {requests.map((request) => {
                  const fromLocation = request.from_location_detail || request.from_location;
                  const toLocation = request.to_location_detail || request.to_location;
                  const fromCity = fromLocation?.city || fromLocation?.label || 'N/A';
                  const toCity = toLocation?.city || toLocation?.label || 'N/A';
                  const offersCount = getVisibleOffersCount(request.id);

                  return (
                    <Card
                      key={request.id}
                      className="group border-border bg-card/50 hover:border-secondary hover:shadow-lg transition-all duration-300 cursor-pointer overflow-hidden"
                      onClick={() => navigate(`/client/operations/${request.id}`)}
                    >
                      <CardContent className="p-6">
                        <div className="flex flex-col gap-4">
                          {/* En-tête avec ID et badges */}
                          <div className="flex items-start justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <h3 className="text-lg font-bold text-foreground">Demande #{request.id.slice(-8)}</h3>
                                {getStatusBadge(request.status)}
                                <Badge variant="secondary" className={`font-semibold ${offersCount > 0 ? 'bg-blue-500/20 text-blue-400 border-blue-500/30' : 'bg-gray-500/20 text-gray-400 border-gray-500/30'}`}>
                                  {offersCount} {offersCount === 1 ? 'offre' : 'offres'}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2 flex-wrap">
                                {getUrgencyBadge(request.urgency_level)}
                                {getPaymentTermsBadge(request.payment_terms)}
                              </div>
                            </div>

                            <DropdownMenu>
                              <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => navigate(`/client/operations/${request.id}`)}>
                                  <Eye className="w-4 h-4 mr-2" />
                                  Voir détails
                                </DropdownMenuItem>
                                {request.status === 'DRAFT' && (
                                  <>
                                    <DropdownMenuItem onClick={() => handleEditRequest(request)}>
                                      <Edit className="w-4 h-4 mr-2" />
                                      Modifier
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handlePublishRequest(request.id)}>
                                      <Send className="w-4 h-4 mr-2" />
                                      Publier
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setSelectedRequest(request);
                                        setDeleteDialogOpen(true);
                                      }}
                                      className="text-destructive"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Supprimer
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {request.status === 'PUBLISHED' && (
                                  <>
                                    <DropdownMenuItem onClick={() => handleEditRequest(request)}>
                                      <Edit className="w-4 h-4 mr-2" />
                                      Modifier (dépublier)
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={async () => {
                                        const result = await unpublishRequest(request.id);
                                        if (result.success) {
                                          toast({
                                            title: 'Demande dépubliée',
                                            description: 'La demande a été dépubliée et toutes les offres ont été annulées.',
                                          });
                                        } else {
                                          toast({
                                            title: 'Erreur',
                                            description: result.error || 'Erreur lors de la dépublication de la demande.',
                                            variant: 'destructive',
                                          });
                                        }
                                      }}
                                    >
                                      <X className="w-4 h-4 mr-2" />
                                      Dépublier
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleCancelRequest(request.id)}
                                      className="text-destructive"
                                    >
                                      <XCircle className="w-4 h-4 mr-2" />
                                      Annuler
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setSelectedRequest(request);
                                        setDeleteDialogOpen(true);
                                      }}
                                      className="text-destructive"
                                    >
                                      <Trash2 className="w-4 h-4 mr-2" />
                                      Supprimer
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {request.status === 'ASSIGNED' && (
                                  <DropdownMenuItem onClick={() => handleTabChange('tracking')}>
                                    <Navigation className="w-4 h-4 mr-2" />
                                    Suivre
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>

                          {/* Trajet */}
                          <div className="flex items-center gap-3 p-3 bg-primary/5 rounded-lg border border-border/50">
                            <div className="flex-shrink-0">
                              <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                                <MapPin className="w-5 h-5 text-primary" />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 text-sm">
                                <span className="font-semibold text-foreground truncate">{fromCity}</span>
                                <span className="text-muted-foreground flex-shrink-0">→</span>
                                <span className="font-semibold text-foreground truncate">{toCity}</span>
                              </div>
                            </div>
                          </div>

                          {/* Informations supplémentaires */}
                          <div className="grid grid-cols-2 gap-3 text-sm">
                            {request.preferred_pickup_date && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Calendar className="w-4 h-4 flex-shrink-0" />
                                <span className="truncate">Départ: {formatDateOnly(request.preferred_pickup_date)}</span>
                              </div>
                            )}
                            {request.preferred_delivery_date && (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <Calendar className="w-4 h-4 flex-shrink-0" />
                                <span className="truncate">Arrivée: {formatDateOnly(request.preferred_delivery_date)}</span>
                              </div>
                            )}
                            {request.declared_value && (
                              <div className="flex items-center gap-2 text-muted-foreground col-span-2">
                                <DollarSign className="w-4 h-4 flex-shrink-0" />
                                <span>Valeur déclarée: <span className="font-semibold text-secondary">{formatCurrency(request.declared_value)}</span></span>
                              </div>
                            )}
                            <div className="flex items-center gap-2 text-muted-foreground col-span-2">
                              <Clock className="w-4 h-4 flex-shrink-0" />
                              <span>Créée le {formatDate(request.created_at)}</span>
                            </div>
                          </div>

                          {/* Actions rapides */}
                          {request.status === 'DRAFT' && (
                            <div className="pt-2 border-t border-border">
                              <Button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handlePublishRequest(request.id);
                                }}
                                className="w-full bg-green-600 hover:bg-green-700"
                                size="sm"
                              >
                                <Send className="w-4 h-4 mr-2" />
                                Publier la demande
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="tracking" className="space-y-4">
            {filteredActiveRequests.length === 0 ? (
              <Card className="bg-card/50 border-border">
                <CardContent className="p-12 text-center">
                  <Navigation className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Aucune livraison en cours</h3>
                  <p className="text-muted-foreground">Vous n'avez pas de demandes en transit actuellement.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-6">
                {/* Sélection de la demande à suivre */}
                <Card className="bg-card/50 border-border">
                  <CardContent className="p-6">
                    <h2 className="text-lg font-semibold mb-4">Sélectionner une demande à suivre</h2>
                    <div className="grid gap-2">
                      {filteredActiveRequests.map((request) => (
                        <Button
                          key={request.id}
                          variant={selectedRequestId === request.id ? 'default' : 'outline'}
                          onClick={() => setSelectedRequestId(request.id)}
                          className="justify-start"
                        >
                          <Package className="w-4 h-4 mr-2" />
                          Demande #{request.id.slice(-8)} - {request.from_location?.city || request.from_location?.label || 'N/A'} → {request.to_location?.city || request.to_location?.label || 'N/A'}
                        </Button>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                {/* Suivi détaillé */}
                {selectedRequestId && (
                  <Card className="bg-card/50 border-border">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-lg font-semibold">Événements de suivi</h2>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fetchTrackingData(selectedRequestId)}
                          disabled={trackingLoading}
                        >
                          {trackingLoading ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Navigation className="w-4 h-4 mr-2" />
                          )}
                          Actualiser
                        </Button>
                      </div>

                      {trackingLoading && trackingEvents.length === 0 ? (
                        <div className="flex items-center justify-center h-32">
                          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                        </div>
                      ) : trackingEvents.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                          <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                          <p>Aucun événement de suivi disponible pour le moment.</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {trackingEvents.map((event, index) => (
                            <div
                              key={event.id || index}
                              className="flex gap-4 p-4 border border-border rounded-lg bg-primary-700/30"
                            >
                              <div className="flex-shrink-0">
                                <div className="w-10 h-10 rounded-full bg-primary-600 flex items-center justify-center">
                                  <CheckCircle className="w-5 h-5 text-white" />
                                </div>
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-2">
                                  {getTrackingStatusBadge(event.status)}
                                  <span className="text-sm text-muted-foreground">
                                    {formatDate(event.timestamp || event.created_at)}
                                  </span>
                                </div>
                                {event.location && (
                                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                                    <MapPin className="w-4 h-4" />
                                    <span>{event.location}</span>
                                  </div>
                                )}
                                {event.description && (
                                  <p className="text-sm text-muted-foreground">{event.description}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </TabsContent>

          <TabsContent value="bookings" className="space-y-6">
            {/* Sous-onglets pour Réservations actives / Historique */}
            <Tabs value={bookingSubTab} onValueChange={setBookingSubTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-6">
                <TabsTrigger value="active" className="flex items-center gap-2">
                  <Package className="w-4 h-4" />
                  Mes Réservations
                </TabsTrigger>
                <TabsTrigger value="history" className="flex items-center gap-2">
                  <History className="w-4 h-4" />
                  Historique
                </TabsTrigger>
              </TabsList>

              {/* Sous-onglet: Mes Réservations (actives) */}
              <TabsContent value="active" className="space-y-6">
                {/* Filtres */}
                <Card className="bg-card border border-border">
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex-1">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                          <Input
                            placeholder="Rechercher une réservation..."
                            value={bookingFilters.search}
                            onChange={(e) => setBookingFilters(prev => ({ ...prev, search: e.target.value }))}
                            className="pl-10"
                          />
                        </div>
                      </div>
                      <Select
                        value={bookingFilters.status_code}
                        onValueChange={(value) => setBookingFilters(prev => ({ ...prev, status_code: value }))}
                      >
                        <SelectTrigger className="w-full sm:w-48">
                          <SelectValue placeholder="Filtrer par statut" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tous les statuts</SelectItem>
                          {constants?.booking_status?.filter(([value]) =>
                            ['CONFIRMED', 'IN_PROGRESS', 'DELIVERED'].includes(value)
                          ).map(([value, label]) => (
                            <SelectItem key={value} value={value}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={bookingFilters.payment_status}
                        onValueChange={(value) => setBookingFilters(prev => ({ ...prev, payment_status: value }))}
                      >
                        <SelectTrigger className="w-full sm:w-48">
                          <SelectValue placeholder="Filtrer par paiement" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tous les paiements</SelectItem>
                          <SelectItem value="paid">Payé totalement</SelectItem>
                          <SelectItem value="partial">Payé partiellement</SelectItem>
                          <SelectItem value="unpaid">Non payé</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                {/* Liste des réservations actives */}
                {bookingsLoading ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-4" />
                      <div className="text-muted-foreground">Chargement des réservations...</div>
                    </CardContent>
                  </Card>
                ) : bookingsError ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">Erreur</h3>
                      <div className="text-destructive">{bookingsError}</div>
                    </CardContent>
                  </Card>
                ) : bookings.filter(booking => {
                  const activeStatuses = ['CONFIRMED', 'IN_PROGRESS', 'DELIVERED'];
                  return activeStatuses.includes(booking.status_code || booking.status);
                }).length === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <Package className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">Aucune réservation active</h3>
                      <p className="text-muted-foreground">Vous n'avez pas encore de réservations actives.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {bookings
                      .filter(booking => {
                        // Filtrer par statut actif (pour le sous-onglet actif)
                        const activeStatuses = ['CONFIRMED', 'IN_PROGRESS', 'DELIVERED'];
                        if (bookingSubTab === 'active' && !activeStatuses.includes(booking.status_code || booking.status)) {
                          return false;
                        }
                        if (bookingSubTab === 'history' && activeStatuses.includes(booking.status_code || booking.status)) {
                          return false;
                        }

                        // Filtre par recherche
                        if (bookingFilters.search) {
                          const searchLower = bookingFilters.search.toLowerCase();
                          const request = booking.request_detail || booking.request;
                          const matchesSearch =
                            booking.id?.toLowerCase().includes(searchLower) ||
                            request?.from_location?.city?.toLowerCase().includes(searchLower) ||
                            request?.from_location?.label?.toLowerCase().includes(searchLower) ||
                            request?.to_location?.city?.toLowerCase().includes(searchLower) ||
                            request?.to_location?.label?.toLowerCase().includes(searchLower) ||
                            booking.vehicle_detail?.plate_number?.toLowerCase().includes(searchLower) ||
                            booking.vehicle?.plate_number?.toLowerCase().includes(searchLower) ||
                            formatCurrency(booking.agreed_price || booking.price).toLowerCase().includes(searchLower);
                          if (!matchesSearch) return false;
                        }

                        // Filtre par statut
                        if (bookingFilters.status_code !== 'all' && (booking.status_code || booking.status) !== bookingFilters.status_code) {
                          return false;
                        }

                        // Filtre par statut de paiement
                        if (bookingFilters.payment_status !== 'all') {
                          const paymentPercentage = booking.payment_percentage || 0;
                          if (bookingFilters.payment_status === 'paid' && paymentPercentage < 100) return false;
                          if (bookingFilters.payment_status === 'partial' && (paymentPercentage === 0 || paymentPercentage >= 100)) return false;
                          if (bookingFilters.payment_status === 'unpaid' && paymentPercentage > 0) return false;
                        }

                        return true;
                      })
                      .map((booking) => {
                        const request = booking.request_detail || booking.request;
                        const transporter = booking.transporter_detail || booking.transporter;
                        const driver = booking.driver_detail || booking.driver;
                        const payments = booking.payments || [];

                        return (
                          <Card key={booking.id} className="bg-card border border-border">
                            <CardContent className="p-4 space-y-3">
                              {/* En-tête simple : RESERVATION et PAYMENT */}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">RESERVATION</span>
                                  <span className="text-sm font-bold">#{booking.id?.slice(-8) || 'N/A'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">PAYMENT</span>
                                  <span className="text-sm font-bold text-yellow-400">{formatCurrency(booking.agreed_price || booking.price)}</span>
                                </div>
                              </div>

                              {/* Badges de statut */}
                              <div className="flex items-center gap-2 flex-wrap">
                                {getBookingStatusBadge(booking.status_code || booking.status)}
                                {getPaymentStatusBadge(booking)}
                              </div>

                              {/* Barre de progression */}
                              {booking.payment_percentage !== undefined && (
                                <div>
                                  <div className="w-full bg-muted rounded-full h-2 overflow-hidden mb-1">
                                    <div
                                      className={`h-full rounded-full transition-all duration-500 ${
                                        booking.payment_percentage >= 100 ? 'bg-green-500' :
                                        booking.payment_percentage > 0 ? 'bg-yellow-500' : 'bg-red-500'
                                      }`}
                                      style={{ width: `${Math.min(booking.payment_percentage, 100)}%` }}
                                    />
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {booking.payment_percentage}% ({formatCurrency(booking.total_paid || 0)} / {formatCurrency(booking.agreed_price || booking.price)})
                                  </div>
                                </div>
                              )}

                              {/* Visualisation de route simple */}
                              <div className="flex items-center gap-2 py-2">
                                <div className="flex flex-col items-center flex-1">
                                  <MapPin className="w-5 h-5 text-green-500" />
                                  <span className="text-xs font-medium mt-1">
                                    {request?.from_location_detail?.city || request?.from_location_detail?.label ||
                                     request?.from_location?.city || request?.from_location?.label || 'N/A'}
                                  </span>
                                </div>
                                <div className="flex-1 flex items-center justify-center">
                                  <div className="flex-1 border-t-2 border-dashed border-muted-foreground/30"></div>
                                  <Truck className="w-5 h-5 text-green-500 mx-2" />
                                  <div className="flex-1 border-t-2 border-dashed border-muted-foreground/30"></div>
                                </div>
                                <div className="flex flex-col items-center flex-1">
                                  <Navigation className="w-5 h-5 text-green-500" />
                                  <span className="text-xs font-medium mt-1">
                                    {request?.to_location_detail?.city || request?.to_location_detail?.label ||
                                     request?.to_location?.city || request?.to_location?.label || 'N/A'}
                                  </span>
                                </div>
                              </div>

                              {/* Informations en deux colonnes */}
                              <div className="grid grid-cols-2 gap-4 pt-2 border-t border-border">
                                {/* Colonne Transport */}
                                <div>
                                  <div className="flex items-center gap-1.5 mb-2">
                                    <Truck className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-xs font-semibold">Transport</span>
                                  </div>
                                  <div className="space-y-1 text-xs">
                                    {request?.preferred_pickup_date && (
                                      <div>
                                        <span className="text-muted-foreground">Départ: </span>
                                        <span className="font-medium">{formatDateOnly(request.preferred_pickup_date)}</span>
                                      </div>
                                    )}
                                    {request?.preferred_delivery_date && (
                                      <div>
                                        <span className="text-muted-foreground">Arrivée: </span>
                                        <span className="font-medium">{formatDateOnly(request.preferred_delivery_date)}</span>
                                      </div>
                                    )}
                                    <div>
                                      <span className="text-muted-foreground">Véhicule: </span>
                                      <span className="font-medium">{booking.vehicle_detail?.plate_number || booking.vehicle?.plate_number || '-'}</span>
                                    </div>
                                  </div>
                                </div>

                                {/* Colonne Transporteur */}
                                <div>
                                  <div className="flex items-center gap-1.5 mb-2">
                                    <User className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-xs font-semibold">Transporteur</span>
                                  </div>
                                  <div className="space-y-1 text-xs">
                                    <div>
                                      <span className="text-muted-foreground">Chauffeur: </span>
                                      <span className="font-medium">
                                        {driver?.full_name ||
                                         (driver?.first_name && driver?.last_name
                                           ? `${driver.first_name} ${driver.last_name}`.trim()
                                           : driver?.first_name || '-')}
                                      </span>
                                    </div>
                                    {driver?.phone && (
                                      <div>
                                        <span className="text-muted-foreground">Tél: </span>
                                        <span className="font-medium">{driver.phone}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Termes */}
                              {request?.payment_terms && (
                                <div className="pt-2 border-t border-border">
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <FileText className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-xs font-semibold">Termes</span>
                                  </div>
                                  <div className="text-xs pl-5">
                                    <span className="text-muted-foreground">Paiement: </span>
                                    <span className="font-medium">{getPaymentTermsLabel(request.payment_terms)}</span>
                                  </div>
                                </div>
                              )}

                              {/* Actions */}
                              <div className="flex items-center gap-2 pt-2 border-t border-border">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewPayments(booking.id)}
                                  className="flex-1"
                                >
                                  <CreditCard className="w-4 h-4 mr-2" />
                                  Paiements ({payments.length})
                                </Button>
                                {booking.payment_percentage !== undefined && booking.payment_percentage < 100 && (
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => handlePayBooking(booking)}
                                    className="flex-1 bg-green-600 hover:bg-green-700"
                                  >
                                    <Wallet className="w-4 h-4 mr-2" />
                                    Payer
                                  </Button>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                  </div>
                )}
              </TabsContent>

              {/* Sous-onglet: Historique (terminées) */}
              <TabsContent value="history" className="space-y-6">
                {/* Filtres */}
                <Card className="bg-card border border-border">
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row gap-4">
                      <div className="flex-1">
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                          <Input
                            placeholder="Rechercher une réservation..."
                            value={bookingFilters.search}
                            onChange={(e) => setBookingFilters(prev => ({ ...prev, search: e.target.value }))}
                            className="pl-10"
                          />
                        </div>
                      </div>
                      <Select
                        value={bookingFilters.status_code}
                        onValueChange={(value) => setBookingFilters(prev => ({ ...prev, status_code: value }))}
                      >
                        <SelectTrigger className="w-full sm:w-48">
                          <SelectValue placeholder="Filtrer par statut" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tous les statuts</SelectItem>
                          {constants?.booking_status?.filter(([value]) =>
                            ['COMPLETED', 'CANCELLED', 'DISPUTED'].includes(value)
                          ).map(([value, label]) => (
                            <SelectItem key={value} value={value}>{label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={bookingFilters.payment_status}
                        onValueChange={(value) => setBookingFilters(prev => ({ ...prev, payment_status: value }))}
                      >
                        <SelectTrigger className="w-full sm:w-48">
                          <SelectValue placeholder="Filtrer par paiement" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tous les paiements</SelectItem>
                          <SelectItem value="paid">Payé totalement</SelectItem>
                          <SelectItem value="partial">Payé partiellement</SelectItem>
                          <SelectItem value="unpaid">Non payé</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </CardContent>
                </Card>

                {/* Liste des réservations historiques */}
                {bookingsLoading ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <Loader2 className="w-8 h-8 animate-spin text-muted-foreground mx-auto mb-4" />
                      <div className="text-muted-foreground">Chargement de l'historique...</div>
                    </CardContent>
                  </Card>
                ) : bookingsError ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">Erreur</h3>
                      <div className="text-destructive">{bookingsError}</div>
                    </CardContent>
                  </Card>
                ) : bookings.filter(booking => {
                  const activeStatuses = ['CONFIRMED', 'IN_PROGRESS', 'DELIVERED'];
                  return !activeStatuses.includes(booking.status_code || booking.status);
                }).length === 0 ? (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <History className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">Aucune réservation dans l'historique</h3>
                      <p className="text-muted-foreground">Vous n'avez pas encore de réservations terminées ou annulées.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {bookings
                      .filter(booking => {
                        // Filtrer uniquement les réservations terminées (historique)
                        const activeStatuses = ['CONFIRMED', 'IN_PROGRESS', 'DELIVERED'];
                        if (activeStatuses.includes(booking.status_code || booking.status)) {
                          return false;
                        }

                        // Filtre par recherche
                        if (bookingFilters.search) {
                          const searchLower = bookingFilters.search.toLowerCase();
                          const request = booking.request_detail || booking.request;
                          const matchesSearch =
                            booking.id?.toLowerCase().includes(searchLower) ||
                            request?.from_location?.city?.toLowerCase().includes(searchLower) ||
                            request?.from_location?.label?.toLowerCase().includes(searchLower) ||
                            request?.to_location?.city?.toLowerCase().includes(searchLower) ||
                            request?.to_location?.label?.toLowerCase().includes(searchLower) ||
                            booking.vehicle_detail?.plate_number?.toLowerCase().includes(searchLower) ||
                            booking.vehicle?.plate_number?.toLowerCase().includes(searchLower) ||
                            formatCurrency(booking.agreed_price || booking.price).toLowerCase().includes(searchLower);
                          if (!matchesSearch) return false;
                        }

                        // Filtre par statut
                        if (bookingFilters.status_code !== 'all' && (booking.status_code || booking.status) !== bookingFilters.status_code) {
                          return false;
                        }

                        // Filtre par statut de paiement
                        if (bookingFilters.payment_status !== 'all') {
                          const paymentPercentage = booking.payment_percentage || 0;
                          if (bookingFilters.payment_status === 'paid' && paymentPercentage < 100) return false;
                          if (bookingFilters.payment_status === 'partial' && (paymentPercentage === 0 || paymentPercentage >= 100)) return false;
                          if (bookingFilters.payment_status === 'unpaid' && paymentPercentage > 0) return false;
                        }

                        return true;
                      })
                      .map((booking) => {
                        const request = booking.request_detail || booking.request;
                        const transporter = booking.transporter_detail || booking.transporter;
                        const driver = booking.driver_detail || booking.driver;
                        const payments = booking.payments || [];

                        return (
                          <Card key={booking.id} className="bg-card border border-border">
                            <CardContent className="p-4 space-y-3">
                              {/* En-tête simple : RESERVATION et PAYMENT */}
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">RESERVATION</span>
                                  <span className="text-sm font-bold">#{booking.id?.slice(-8) || 'N/A'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-muted-foreground">PAYMENT</span>
                                  <span className="text-sm font-bold text-yellow-400">{formatCurrency(booking.agreed_price || booking.price)}</span>
                                </div>
                              </div>

                              {/* Badges de statut */}
                              <div className="flex items-center gap-2 flex-wrap">
                                {getBookingStatusBadge(booking.status_code || booking.status)}
                                {getPaymentStatusBadge(booking)}
                              </div>

                              {/* Barre de progression */}
                              {booking.payment_percentage !== undefined && (
                                <div>
                                  <div className="w-full bg-muted rounded-full h-2 overflow-hidden mb-1">
                                    <div
                                      className={`h-full rounded-full transition-all duration-500 ${
                                        booking.payment_percentage >= 100 ? 'bg-green-500' :
                                        booking.payment_percentage > 0 ? 'bg-yellow-500' : 'bg-red-500'
                                      }`}
                                      style={{ width: `${Math.min(booking.payment_percentage, 100)}%` }}
                                    />
                                  </div>
                                  <div className="text-xs text-muted-foreground">
                                    {booking.payment_percentage}% ({formatCurrency(booking.total_paid || 0)} / {formatCurrency(booking.agreed_price || booking.price)})
                                  </div>
                                </div>
                              )}

                              {/* Visualisation de route simple */}
                              <div className="flex items-center gap-2 py-2">
                                <div className="flex flex-col items-center flex-1">
                                  <MapPin className="w-5 h-5 text-green-500" />
                                  <span className="text-xs font-medium mt-1">
                                    {request?.from_location_detail?.city || request?.from_location_detail?.label ||
                                     request?.from_location?.city || request?.from_location?.label || 'N/A'}
                                  </span>
                                </div>
                                <div className="flex-1 flex items-center justify-center">
                                  <div className="flex-1 border-t-2 border-dashed border-muted-foreground/30"></div>
                                  <Truck className="w-5 h-5 text-green-500 mx-2" />
                                  <div className="flex-1 border-t-2 border-dashed border-muted-foreground/30"></div>
                                </div>
                                <div className="flex flex-col items-center flex-1">
                                  <Navigation className="w-5 h-5 text-green-500" />
                                  <span className="text-xs font-medium mt-1">
                                    {request?.to_location_detail?.city || request?.to_location_detail?.label ||
                                     request?.to_location?.city || request?.to_location?.label || 'N/A'}
                                  </span>
                                </div>
                              </div>

                              {/* Informations en deux colonnes */}
                              <div className="grid grid-cols-2 gap-4 pt-2 border-t border-border">
                                {/* Colonne Transport */}
                                <div>
                                  <div className="flex items-center gap-1.5 mb-2">
                                    <Truck className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-xs font-semibold">Transport</span>
                                  </div>
                                  <div className="space-y-1 text-xs">
                                    {request?.preferred_pickup_date && (
                                      <div>
                                        <span className="text-muted-foreground">Départ: </span>
                                        <span className="font-medium">{formatDateOnly(request.preferred_pickup_date)}</span>
                                      </div>
                                    )}
                                    {request?.preferred_delivery_date && (
                                      <div>
                                        <span className="text-muted-foreground">Arrivée: </span>
                                        <span className="font-medium">{formatDateOnly(request.preferred_delivery_date)}</span>
                                      </div>
                                    )}
                                    <div>
                                      <span className="text-muted-foreground">Véhicule: </span>
                                      <span className="font-medium">{booking.vehicle_detail?.plate_number || booking.vehicle?.plate_number || '-'}</span>
                                    </div>
                                  </div>
                                </div>

                                {/* Colonne Transporteur */}
                                <div>
                                  <div className="flex items-center gap-1.5 mb-2">
                                    <User className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-xs font-semibold">Transporteur</span>
                                  </div>
                                  <div className="space-y-1 text-xs">
                                    <div>
                                      <span className="text-muted-foreground">Chauffeur: </span>
                                      <span className="font-medium">
                                        {driver?.full_name ||
                                         (driver?.first_name && driver?.last_name
                                           ? `${driver.first_name} ${driver.last_name}`.trim()
                                           : driver?.first_name || '-')}
                                      </span>
                                    </div>
                                    {driver?.phone && (
                                      <div>
                                        <span className="text-muted-foreground">Tél: </span>
                                        <span className="font-medium">{driver.phone}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>

                              {/* Termes */}
                              {request?.payment_terms && (
                                <div className="pt-2 border-t border-border">
                                  <div className="flex items-center gap-1.5 mb-1">
                                    <FileText className="w-4 h-4 text-muted-foreground" />
                                    <span className="text-xs font-semibold">Termes</span>
                                  </div>
                                  <div className="text-xs pl-5">
                                    <span className="text-muted-foreground">Paiement: </span>
                                    <span className="font-medium">{getPaymentTermsLabel(request.payment_terms)}</span>
                                  </div>
                                </div>
                              )}

                              {/* Actions */}
                              <div className="flex items-center gap-2 pt-2 border-t border-border">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleViewPayments(booking.id)}
                                  className="flex-1"
                                >
                                  <CreditCard className="w-4 h-4 mr-2" />
                                  Paiements ({payments.length})
                                </Button>
                                {booking.payment_percentage !== undefined && booking.payment_percentage < 100 && (
                                  <Button
                                    variant="default"
                                    size="sm"
                                    onClick={() => handlePayBooking(booking)}
                                    className="flex-1 bg-green-600 hover:bg-green-700"
                                  >
                                    <Wallet className="w-4 h-4 mr-2" />
                                    Payer
                                  </Button>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="finances" className="space-y-4">
            {/* Statistiques */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="bg-card/50 border-border">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Total payé</p>
                      <p className="text-3xl font-bold text-primary">{formatCurrency(totalPaid)}</p>
                    </div>
                    <TrendingUp className="w-8 h-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-card/50 border-border">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">En attente</p>
                      <p className="text-3xl font-bold text-yellow-500">{formatCurrency(pendingAmount)}</p>
                    </div>
                    <Clock className="w-8 h-8 text-yellow-500" />
                  </div>
                </CardContent>
              </Card>
              <Card className="bg-card/50 border-border">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-2">Factures</p>
                      <p className="text-3xl font-bold text-white">{invoices.length}</p>
                    </div>
                    <FileText className="w-8 h-8 text-blue-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Filtres */}
            <Card className="bg-card/50 border-border">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                      <Input
                        placeholder="Rechercher un paiement..."
                        value={financeFilters.search}
                        onChange={(e) => setFinanceFilters(prev => ({ ...prev, search: e.target.value }))}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <Select value={financeFilters.status} onValueChange={(value) => setFinanceFilters(prev => ({ ...prev, status: value }))}>
                    <SelectTrigger className="w-full sm:w-48">
                      <SelectValue placeholder="Statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="PENDING">En attente</SelectItem>
                      <SelectItem value="SUCCESS">Réussi</SelectItem>
                      <SelectItem value="FAILED">Échoué</SelectItem>
                      <SelectItem value="REFUNDED">Remboursé</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={financeFilters.method} onValueChange={(value) => setFinanceFilters(prev => ({ ...prev, method: value }))}>
                    <SelectTrigger className="w-full sm:w-48">
                      <SelectValue placeholder="Méthode" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les méthodes</SelectItem>
                      <SelectItem value="ORANGE_MONEY">Orange Money</SelectItem>
                      <SelectItem value="MTN_MOMO">MTN Mobile Money</SelectItem>
                      <SelectItem value="BANK_TRANSFER">Virement bancaire</SelectItem>
                      <SelectItem value="CARD">Carte bancaire</SelectItem>
                      <SelectItem value="CASH">Espèces</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Tabs value={financeActiveTab} onValueChange={setFinanceActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger
                  value="payments"
                  className="flex items-center gap-2 data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground"
                >
                  <CreditCard className="w-4 h-4" />
                  Paiements
                  {badgeStats.payments > 0 && (
                    <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5 text-[10px]">
                      {badgeStats.payments > 99 ? '99+' : badgeStats.payments}
                    </Badge>
                  )}
                </TabsTrigger>
                <TabsTrigger
                  value="invoices"
                  className="flex items-center gap-2 data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground"
                >
                  <FileText className="w-4 h-4" />
                  Factures
                  {badgeStats.invoices > 0 && (
                    <Badge variant="secondary" className="ml-1 h-5 min-w-[20px] px-1.5 text-[10px]">
                      {badgeStats.invoices > 99 ? '99+' : badgeStats.invoices}
                    </Badge>
                  )}
                </TabsTrigger>
              </TabsList>

              <TabsContent value="payments" className="space-y-4">
                {paymentsLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : paymentsError ? (
                  <Card className="bg-card/50 border-border">
                    <CardContent className="p-12 text-center">
                      <AlertCircle className="w-16 h-16 text-destructive mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">Erreur</h3>
                      <p className="text-muted-foreground">{paymentsError}</p>
                    </CardContent>
                  </Card>
                ) : payments.length === 0 ? (
                  <Card className="bg-card/50 border-border">
                    <CardContent className="p-12 text-center">
                      <DollarSign className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">Aucun paiement</h3>
                      <p className="text-muted-foreground">Vous n'avez pas encore effectué de paiement.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {payments.map((payment) => (
                      <Card key={payment.id} className="bg-card/50 border-border hover:border-primary/50 transition-colors">
                        <CardContent className="p-6">
                          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-3 flex-wrap">
                                <h3 className="text-lg font-semibold">Paiement #{payment.id.slice(-8)}</h3>
                                {getPaymentStatusBadgeForPayment(payment.status)}
                                <Badge variant="outline" className="flex items-center gap-1">
                                  {getPaymentMethodIcon(payment.method_code || payment.method)}
                                  <span>{payment.method_code || payment.method || 'N/A'}</span>
                                </Badge>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
                                <div className="flex items-center gap-2">
                                  <DollarSign className="w-4 h-4" />
                                  <span>Montant: {formatCurrency(payment.amount)}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4" />
                                  <span>{formatDate(payment.created_at)}</span>
                                </div>
                                {payment.booking_id && (
                                  <div className="flex items-center gap-2">
                                    <Package className="w-4 h-4" />
                                    <span>Réservation: {payment.booking_id.slice(-8)}</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="flex gap-2">
                              {payment.status === 'PENDING' && (
                                <Button
                                  onClick={() => handleInitiatePayment(payment.id)}
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                >
                                  <CreditCard className="w-4 h-4 mr-2" />
                                  Payer
                                </Button>
                              )}
                              <Button variant="outline" size="sm">
                                <Eye className="w-4 h-4 mr-2" />
                                Détails
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="invoices" className="space-y-4">
                {paymentsLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : invoices.length === 0 ? (
                  <Card className="bg-card/50 border-border">
                    <CardContent className="p-12 text-center">
                      <FileText className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">Aucune facture</h3>
                      <p className="text-muted-foreground">Vous n'avez pas encore de factures.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {invoices.map((invoice) => (
                      <Card key={invoice.id} className="bg-card/50 border-border hover:border-primary/50 transition-colors">
                        <CardContent className="p-6">
                          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-3 flex-wrap">
                                <h3 className="text-lg font-semibold">Facture #{invoice.id.slice(-8)}</h3>
                                <Badge variant="outline">{invoice.invoice_number || invoice.id.slice(-8)}</Badge>
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
                                <div className="flex items-center gap-2">
                                  <DollarSign className="w-4 h-4" />
                                  <span>Montant: {formatCurrency(invoice.total_amount)}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Clock className="w-4 h-4" />
                                  <span>{formatDate(invoice.created_at)}</span>
                                </div>
                              </div>
                            </div>

                            <div className="flex gap-2">
                              <Button variant="outline" size="sm">
                                <Eye className="w-4 h-4 mr-2" />
                                Voir
                              </Button>
                              <Button variant="outline" size="sm">
                                <Download className="w-4 h-4 mr-2" />
                                Télécharger
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </TabsContent>

          <TabsContent value="history" className="space-y-4">
            {/* Filtres */}
            <Card className="bg-card/50 border-border">
              <CardContent className="p-6">
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                      <Input
                        placeholder="Rechercher dans l'historique..."
                        value={historyFilters.search}
                        onChange={(e) => setHistoryFilters(prev => ({ ...prev, search: e.target.value }))}
                        className="pl-10"
                      />
                    </div>
                  </div>
                  <Select value={historyFilters.status} onValueChange={(value) => setHistoryFilters(prev => ({ ...prev, status: value }))}>
                    <SelectTrigger className="w-full sm:w-48">
                      <SelectValue placeholder="Statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tous les statuts</SelectItem>
                      <SelectItem value="COMPLETED">Terminées</SelectItem>
                      <SelectItem value="CANCELLED">Annulées</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={historyFilters.date_range} onValueChange={(value) => setHistoryFilters(prev => ({ ...prev, date_range: value }))}>
                    <SelectTrigger className="w-full sm:w-48">
                      <SelectValue placeholder="Période" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Toutes les périodes</SelectItem>
                      <SelectItem value="today">Aujourd'hui</SelectItem>
                      <SelectItem value="week">Cette semaine</SelectItem>
                      <SelectItem value="month">Ce mois</SelectItem>
                      <SelectItem value="year">Cette année</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            <Tabs value={historyActiveTab} onValueChange={setHistoryActiveTab} className="w-full">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="requests" className="data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground">
                  Demandes
                </TabsTrigger>
                <TabsTrigger value="transactions" className="data-[state=active]:bg-secondary data-[state=active]:text-secondary-foreground">
                  Transactions
                </TabsTrigger>
              </TabsList>

              <TabsContent value="requests" className="space-y-4">
                {requestsLoading ? (
                  <div className="flex items-center justify-center h-64">
                    <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
                  </div>
                ) : filteredHistoryRequests.length === 0 ? (
                  <Card className="bg-card/50 border-border">
                    <CardContent className="p-12 text-center">
                      <History className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-xl font-semibold mb-2">Aucun historique</h3>
                      <p className="text-muted-foreground">Vous n'avez pas encore de demandes terminées ou annulées.</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="grid gap-4">
                    {filteredHistoryRequests.map((request) => (
                      <Card key={request.id} className="bg-card/50 border-border hover:border-primary/50 transition-colors">
                        <CardContent className="p-6">
                          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-3 flex-wrap">
                                <h3 className="text-lg font-semibold">Demande #{request.id.slice(-8)}</h3>
                                {getStatusBadge(request.status)}
                                {getVisibleOffersCount(request.id) > 0 && (
                                  <Badge variant="secondary" className="bg-blue-500/20 text-blue-400 border-blue-500/30">
                                    {getVisibleOffersCount(request.id)} {getVisibleOffersCount(request.id) === 1 ? 'offre' : 'offres'}
                                  </Badge>
                                )}
                              </div>

                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
                                <div className="flex items-center gap-2">
                                  <MapPin className="w-4 h-4" />
                                  <span>{request.from_location?.city || request.from_location?.label || 'N/A'} → {request.to_location?.city || request.to_location?.label || 'N/A'}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <Calendar className="w-4 h-4" />
                                  <span>Créée le {formatDate(request.created_at)}</span>
                                </div>
                                {request.updated_at && (
                                  <div className="flex items-center gap-2">
                                    <Clock className="w-4 h-4" />
                                    <span>Modifiée le {formatDate(request.updated_at)}</span>
                                  </div>
                                )}
                                {request.declared_value && (
                                  <div className="flex items-center gap-2">
                                    <DollarSign className="w-4 h-4" />
                                    <span>Valeur: {formatCurrency(request.declared_value)}</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="flex gap-2">
                              <Button variant="outline" size="sm">
                                <Eye className="w-4 h-4 mr-2" />
                                Détails
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="transactions" className="space-y-4">
                <Card className="bg-card/50 border-border">
                  <CardContent className="p-12 text-center">
                    <DollarSign className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-xl font-semibold mb-2">Historique des transactions</h3>
                    <p className="text-muted-foreground mb-4">Consultez l'historique de vos paiements dans la section Finances.</p>
                    <Button onClick={() => handleTabChange('finances')}>
                      Voir les paiements
                    </Button>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
          </TabsContent>
        </Tabs>

        {/* Dialog de création */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Nouvelle demande de transport</DialogTitle>
              <DialogDescription>
                Créez une nouvelle demande de transport. Vous pourrez la publier une fois remplie.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Lieu de départ</Label>
                  <Select
                    value={requestData.from_location_id || 'new'}
                    onValueChange={(value) => {
                      if (value === 'new') {
                        setRequestData(prev => ({ ...prev, from_location_id: null, from_location_name: '' }));
                      } else {
                        setRequestData(prev => ({ ...prev, from_location_id: value, from_location_name: '' }));
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner ou créer un lieu" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">➕ Créer un nouveau lieu</SelectItem>
                      {locations.map((location) => (
                        <SelectItem key={location.id} value={location.id}>
                          {location.label || location.city || location.id.slice(-8)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(!requestData.from_location_id || requestData.from_location_id === 'new') && (
                    <Input
                      placeholder="Entrez le nom de la ville (ex: Conakry, Kankan...)"
                      value={requestData.from_location_name}
                      onChange={(e) => setRequestData(prev => ({ ...prev, from_location_name: e.target.value }))}
                    />
                  )}
                </div>
                <div className="space-y-2">
                  <Label>Lieu d'arrivée</Label>
                  <Select
                    value={requestData.to_location_id || 'new'}
                    onValueChange={(value) => {
                      if (value === 'new') {
                        setRequestData(prev => ({ ...prev, to_location_id: null, to_location_name: '' }));
                      } else {
                        setRequestData(prev => ({ ...prev, to_location_id: value, to_location_name: '' }));
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner ou créer un lieu" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="new">➕ Créer un nouveau lieu</SelectItem>
                      {locations.map((location) => (
                        <SelectItem key={location.id} value={location.id}>
                          {location.label || location.city || location.id.slice(-8)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {(!requestData.to_location_id || requestData.to_location_id === 'new') && (
                    <Input
                      placeholder="Entrez le nom de la ville (ex: Conakry, Kankan...)"
                      value={requestData.to_location_name}
                      onChange={(e) => setRequestData(prev => ({ ...prev, to_location_name: e.target.value }))}
                    />
                  )}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date d'enlèvement prévue</Label>
                  <Input
                    type="date"
                    value={requestData.preferred_pickup_date}
                    onChange={(e) => setRequestData(prev => ({ ...prev, preferred_pickup_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date de livraison prévue</Label>
                  <Input
                    type="date"
                    value={requestData.preferred_delivery_date}
                    onChange={(e) => setRequestData(prev => ({ ...prev, preferred_delivery_date: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Valeur déclarée (GNF)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={requestData.declared_value}
                    onChange={(e) => setRequestData(prev => ({ ...prev, declared_value: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Urgence</Label>
                  <Select value={requestData.urgency_level} onValueChange={(value) => setRequestData(prev => ({ ...prev, urgency_level: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NORMAL">Normal</SelectItem>
                      <SelectItem value="URGENT">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Nouveaux champs métier pour la conformité */}
              <div className="space-y-4 border-t pt-4">
                <h3 className="text-sm font-semibold text-foreground">Informations de transport</h3>

                <div className="space-y-2">
                  <Label>Manutention <span className="text-red-500">*</span></Label>
                  <Select
                    value={requestData.handling_responsibility || ''}
                    onValueChange={(value) => setRequestData(prev => ({ ...prev, handling_responsibility: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Qui prend en charge la manutention ?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CLIENT">Le client prend en charge la manutention</SelectItem>
                      <SelectItem value="TRANSPORTER">Le transporteur prend en charge la manutention</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Type de marchandise</Label>
                  <Select
                    value={requestData.goods_type || ''}
                    onValueChange={(value) => setRequestData(prev => ({ ...prev, goods_type: value || null }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez le type de marchandise (optionnel)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Non spécifié</SelectItem>
                      {constants?.transport_domains?.map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Type de véhicule souhaité</Label>
                  <Select
                    value={requestData.preferred_vehicle_type || 'none'}
                    onValueChange={(value) => setRequestData(prev => ({ ...prev, preferred_vehicle_type: value === 'none' ? null : value }))}
                    disabled={loadingVehicleTypes}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={loadingVehicleTypes ? "Chargement..." : "Sélectionnez un type de véhicule (optionnel)"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune préférence</SelectItem>
                      {vehicleTypes.map((vehicleType) => (
                        <SelectItem key={vehicleType.code} value={vehicleType.code}>
                          {vehicleType.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Poids estimatif (kg)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={requestData.estimated_weight_kg}
                      onChange={(e) => setRequestData(prev => ({ ...prev, estimated_weight_kg: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Volume estimatif (m³)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={requestData.estimated_volume_m3}
                      onChange={(e) => setRequestData(prev => ({ ...prev, estimated_volume_m3: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Conditions de paiement</Label>
                <Select value={requestData.payment_terms} onValueChange={(value) => setRequestData(prev => ({ ...prev, payment_terms: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PREPAID">Prépaiement</SelectItem>
                    <SelectItem value="COD">Paiement à la livraison</SelectItem>
                    <SelectItem value="POSTPAID">Paiement différé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Description (optionnel)</Label>
                <Textarea
                  placeholder="Décrivez votre demande..."
                  value={requestData.description}
                  onChange={(e) => setRequestData(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleCreateRequest}>
                Créer la demande
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog d'édition */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Modifier la demande</DialogTitle>
              <DialogDescription>
                Modifiez les informations de votre demande de transport.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date d'enlèvement prévue</Label>
                  <Input
                    type="date"
                    value={requestData.preferred_pickup_date}
                    onChange={(e) => setRequestData(prev => ({ ...prev, preferred_pickup_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Date de livraison prévue</Label>
                  <Input
                    type="date"
                    value={requestData.preferred_delivery_date}
                    onChange={(e) => setRequestData(prev => ({ ...prev, preferred_delivery_date: e.target.value }))}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Valeur déclarée (GNF)</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={requestData.declared_value}
                    onChange={(e) => setRequestData(prev => ({ ...prev, declared_value: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Urgence</Label>
                  <Select value={requestData.urgency_level} onValueChange={(value) => setRequestData(prev => ({ ...prev, urgency_level: value }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NORMAL">Normal</SelectItem>
                      <SelectItem value="URGENT">Urgent</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Nouveaux champs métier pour la conformité */}
              <div className="space-y-4 border-t pt-4">
                <h3 className="text-sm font-semibold text-foreground">Informations de transport</h3>

                <div className="space-y-2">
                  <Label>Manutention <span className="text-red-500">*</span></Label>
                  <Select
                    value={requestData.handling_responsibility || ''}
                    onValueChange={(value) => setRequestData(prev => ({ ...prev, handling_responsibility: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Qui prend en charge la manutention ?" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CLIENT">Le client prend en charge la manutention</SelectItem>
                      <SelectItem value="TRANSPORTER">Le transporteur prend en charge la manutention</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Type de marchandise</Label>
                  <Select
                    value={requestData.goods_type || ''}
                    onValueChange={(value) => setRequestData(prev => ({ ...prev, goods_type: value || null }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionnez le type de marchandise (optionnel)" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Non spécifié</SelectItem>
                      {constants?.transport_domains?.map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Type de véhicule souhaité</Label>
                  <Select
                    value={requestData.preferred_vehicle_type || 'none'}
                    onValueChange={(value) => setRequestData(prev => ({ ...prev, preferred_vehicle_type: value === 'none' ? null : value }))}
                    disabled={loadingVehicleTypes}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={loadingVehicleTypes ? "Chargement..." : "Sélectionnez un type de véhicule (optionnel)"} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucune préférence</SelectItem>
                      {vehicleTypes.map((vehicleType) => (
                        <SelectItem key={vehicleType.code} value={vehicleType.code}>
                          {vehicleType.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Poids estimatif (kg)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={requestData.estimated_weight_kg}
                      onChange={(e) => setRequestData(prev => ({ ...prev, estimated_weight_kg: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Volume estimatif (m³)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="0.00"
                      value={requestData.estimated_volume_m3}
                      onChange={(e) => setRequestData(prev => ({ ...prev, estimated_volume_m3: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Conditions de paiement</Label>
                <Select value={requestData.payment_terms} onValueChange={(value) => setRequestData(prev => ({ ...prev, payment_terms: value }))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PREPAID">Prépaiement</SelectItem>
                    <SelectItem value="COD">Paiement à la livraison</SelectItem>
                    <SelectItem value="POSTPAID">Paiement différé</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Annuler
              </Button>
              <Button onClick={handleUpdateRequest}>
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog de confirmation de suppression */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Êtes-vous sûr ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. La demande sera définitivement supprimée.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteRequest} className="bg-destructive text-destructive-foreground">
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Dialog de détails de l'offre */}
        <Dialog open={offerDetailDialogOpen} onOpenChange={setOfferDetailDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Détails de l'offre</DialogTitle>
              <DialogDescription>
                Informations complètes sur l'offre du transporteur
              </DialogDescription>
            </DialogHeader>
            {selectedOffer && (
              <div className="grid gap-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Prix proposé</Label>
                    <p className="text-2xl font-bold text-primary">{formatCurrency(selectedOffer.admin_display_price || selectedOffer.price)}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Statut</Label>
                    <div className="mt-2">{getOfferStatusBadge(selectedOffer.status)}</div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Transporteur</Label>
                    {/* Informations transporteur masquées */}
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Véhicule</Label>
                    <p className="font-medium">{selectedOffer.vehicle?.plate_number || 'N/A'}</p>
                  </div>
                </div>
                {selectedOffer.estimated_distance_km && (
                  <div>
                    <Label className="text-muted-foreground">Distance estimée</Label>
                    <p className="font-medium">{selectedOffer.estimated_distance_km} km</p>
                  </div>
                )}
                {selectedOffer.message && (
                  <div>
                    <Label className="text-muted-foreground">Message du transporteur</Label>
                    <p className="mt-2 p-3 bg-primary-700/30 rounded-lg">{selectedOffer.message}</p>
                  </div>
                )}
                <div>
                  <Label className="text-muted-foreground">Demande</Label>
                  <p className="font-medium">
                    {selectedOffer.request?.from_location?.city || selectedOffer.request?.from_location?.label || 'N/A'} → {selectedOffer.request?.to_location?.city || selectedOffer.request?.to_location?.label || 'N/A'}
                  </p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setOfferDetailDialogOpen(false)}>
                Fermer
              </Button>
              {selectedOffer?.status === 'PENDING' && (
                <>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setOfferDetailDialogOpen(false);
                      handleRejectOffer(selectedOffer);
                    }}
                    className="border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                  >
                    Rejeter
                  </Button>
                  <Button
                    onClick={() => {
                      setOfferDetailDialogOpen(false);
                      handleAcceptOffer(selectedOffer);
                    }}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Accepter l'offre
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Dialog de confirmation d'acceptation */}
        <AlertDialog open={acceptDialogOpen} onOpenChange={setAcceptDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Accepter l'offre ?</AlertDialogTitle>
              <AlertDialogDescription>
                Vous êtes sur le point d'accepter l'offre de {formatCurrency(selectedOffer?.admin_display_price || selectedOffer?.price)}.
                Cette action peut déclencher un paiement selon les conditions de votre demande.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={confirmAcceptOffer} className="bg-green-600 hover:bg-green-700">
                Confirmer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Dialog de confirmation de rejet */}
        <AlertDialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Rejeter l'offre ?</AlertDialogTitle>
              <AlertDialogDescription>
                Vous êtes sur le point de rejeter l'offre du transporteur. Cette action est irréversible.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction
                onClick={confirmRejectOffer}
                className="bg-destructive text-destructive-foreground"
              >
                Rejeter
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Modal pour les paiements */}
        <Dialog open={paymentDialogOpen} onOpenChange={setPaymentDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5" />
                Paiements - Réservation #{selectedBooking?.id?.slice(-8)}
              </DialogTitle>
            </DialogHeader>

            {selectedBooking && (
              <>
                <div className="mt-2 space-y-1 text-sm text-primary-300">
                  <div>Prix convenu: <span className="font-semibold text-secondary">{formatCurrency(selectedBooking.agreed_price || selectedBooking.price)}</span></div>
                  <div>Total payé: <span className="font-semibold text-secondary">{formatCurrency(selectedBooking.total_paid || 0)}</span> ({selectedBooking.payment_percentage || 0}%)</div>
                </div>

                <div className="mt-4 space-y-4">
                {selectedBooking.payments && selectedBooking.payments.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    Aucun paiement enregistré pour cette réservation
                  </div>
                ) : (
                  <div className="space-y-3">
                    {selectedBooking.payments.map((payment) => (
                      <Card key={payment.id} className="bg-card/50 border-border">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-3 mb-2">
                                <h4 className="font-semibold">Paiement #{payment.id?.slice(-8)}</h4>
                                {getPaymentStatusBadgeForPayment(payment.status)}
                                <Badge variant="outline" className="flex items-center gap-1">
                                  {getPaymentMethodIcon()}
                                  <span>{payment.method_code || payment.method || 'N/A'}</span>
                                </Badge>
                              </div>
                              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                <div>
                                  <Label className="text-muted-foreground text-xs">Montant</Label>
                                  <p className="mt-1 font-semibold text-secondary">{formatCurrency(payment.amount)}</p>
                                </div>
                                <div>
                                  <Label className="text-muted-foreground text-xs">Date</Label>
                                  <p className="mt-1">{formatDate(payment.created_at)}</p>
                                </div>
                                <div>
                                  <Label className="text-muted-foreground text-xs">Référence</Label>
                                  <p className="mt-1 font-mono text-xs">{payment.provider_reference || 'N/A'}</p>
                                </div>
                                {payment.proof_url && (
                                  <div>
                                    <Label className="text-muted-foreground text-xs">Preuve</Label>
                                    <div className="flex gap-2 mt-1">
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleViewProof(payment.proof_url)}
                                      >
                                        <Eye className="w-3 h-3 mr-1" />
                                        Voir
                                      </Button>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={() => handleDownloadProof(payment.proof_url, payment.id)}
                                      >
                                        <Download className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal pour afficher la preuve de paiement */}
        <Dialog open={proofDialogOpen} onOpenChange={setProofDialogOpen}>
          <DialogContent className="max-w-4xl">
            <DialogHeader>
              <DialogTitle>Preuve de paiement</DialogTitle>
            </DialogHeader>
            {selectedProof && (
              <div className="mt-4">
                {selectedProof.toLowerCase().endsWith('.pdf') ? (
                  <iframe
                    src={selectedProof}
                    className="w-full h-[600px] border rounded"
                    title="Preuve de paiement PDF"
                  />
                ) : (
                  <img
                    src={selectedProof}
                    alt="Preuve de paiement"
                    className="w-full h-auto rounded border"
                  />
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Modal pour créer un paiement */}
        <Dialog open={createPaymentDialogOpen} onOpenChange={setCreatePaymentDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader className="pb-4">
              <DialogTitle className="flex items-center gap-3 text-2xl">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <CreditCard className="w-6 h-6 text-primary" />
                </div>
                <span>Nouveau paiement</span>
              </DialogTitle>
              {paymentBooking && (
                <Card className="mt-4 bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Réservation</p>
                        <p className="font-semibold text-sm">#{paymentBooking.id?.slice(-8)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Prix convenu</p>
                        <p className="font-semibold text-sm text-secondary">{formatCurrency(paymentBooking.agreed_price || paymentBooking.price)}</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Total payé</p>
                        <p className="font-semibold text-sm text-secondary">{formatCurrency(paymentBooking.total_paid || 0)}</p>
                        <p className="text-xs text-muted-foreground">({paymentBooking.payment_percentage || 0}%)</p>
                      </div>
                      <div>
                        <p className="text-xs text-muted-foreground mb-1">Montant restant</p>
                        <p className="font-semibold text-sm text-secondary">{formatCurrency((paymentBooking.agreed_price || paymentBooking.price) - (paymentBooking.total_paid || 0))}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </DialogHeader>

            <div className="space-y-6 py-2">
              {/* Section 1: Sélection de la méthode de paiement */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-5 bg-primary rounded-full" />
                  <Label className="text-base font-semibold">Méthode de paiement</Label>
                </div>
                <Card className="p-4">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {constants?.payment_methods?.map(([value, label]) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setPaymentData(prev => ({ ...prev, method_code: value }))}
                        className={`group relative p-4 border-2 rounded-xl transition-all duration-200 ${
                          paymentData.method_code === value
                            ? 'border-primary bg-primary/10 shadow-md scale-[1.02]'
                            : 'border-border hover:border-primary/50 hover:bg-card/80 hover:shadow-sm'
                        }`}
                      >
                        <div className="flex flex-col items-center gap-3">
                          {value === 'CARD' && (
                            <div className="w-20 h-14 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-800 rounded-lg shadow-lg flex items-center justify-center text-white font-bold text-sm transform group-hover:scale-105 transition-transform">
                              VISA
                            </div>
                          )}
                          {value === 'ORANGE_MONEY' && (
                            <div className="w-20 h-14 bg-gradient-to-br from-orange-500 via-orange-600 to-orange-700 rounded-lg shadow-lg flex items-center justify-center">
                              <span className="text-white font-bold text-sm">ORANGE</span>
                            </div>
                          )}
                          {value === 'MTN_MOMO' && (
                            <div className="w-20 h-14 bg-gradient-to-br from-yellow-500 via-yellow-600 to-yellow-700 rounded-lg shadow-lg flex items-center justify-center">
                              <span className="text-white font-bold text-sm">MTN</span>
                            </div>
                          )}
                          {value === 'BANK_TRANSFER' && (
                            <div className="w-20 h-14 bg-gradient-to-br from-green-600 via-green-700 to-green-800 rounded-lg shadow-lg flex items-center justify-center">
                              <Banknote className="w-10 h-10 text-white" />
                            </div>
                          )}
                          {value === 'CASH' && (
                            <div className="w-20 h-14 bg-gradient-to-br from-gray-600 via-gray-700 to-gray-800 rounded-lg shadow-lg flex items-center justify-center">
                              <DollarSign className="w-10 h-10 text-white" />
                            </div>
                          )}
                          <span className="text-sm font-medium text-center">{label}</span>
                          {paymentData.method_code === value && (
                            <div className="absolute top-2 right-2">
                              <div className="w-5 h-5 bg-primary rounded-full flex items-center justify-center">
                                <CheckCircle className="w-3 h-3 text-white" />
                              </div>
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                </Card>
              </div>

              {/* Section 2: Montant */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <div className="w-1 h-5 bg-primary rounded-full" />
                  <Label htmlFor="amount" className="text-base font-semibold">Montant à payer</Label>
                </div>
                <Card className="p-4">
                  <div className="space-y-3">
                    <div className="relative">
                      <div className="absolute left-3 top-1/2 -translate-y-1/2">
                        <DollarSign className="w-5 h-5 text-muted-foreground" />
                      </div>
                      <Input
                        id="amount"
                        type="number"
                        value={paymentData.amount}
                        onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        className="pl-10 text-lg font-semibold h-12"
                      />
                    </div>
                    {paymentBooking && (
                      <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg border border-border">
                        <span className="text-sm text-muted-foreground">Montant restant disponible</span>
                        <span className="text-sm font-semibold text-secondary">
                          {formatCurrency((paymentBooking.agreed_price || paymentBooking.price) - (paymentBooking.total_paid || 0))}
                        </span>
                      </div>
                    )}
                  </div>
                </Card>
              </div>

              {/* Section 3: Formulaires spécifiques selon la méthode de paiement */}
              {paymentData.method_code && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <div className="w-1 h-5 bg-primary rounded-full" />
                    <Label className="text-base font-semibold">Informations de paiement</Label>
                  </div>
                  <Card className="p-5">
                    {/* Formulaire pour Carte bancaire (CARD) */}
                    {paymentData.method_code === 'CARD' && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-border">
                          <CreditCard className="w-5 h-5 text-primary" />
                          <span className="font-semibold">Carte bancaire</span>
                        </div>
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label htmlFor="card_number">Numéro de carte *</Label>
                            <Input
                              id="card_number"
                              type="text"
                              value={paymentData.card_number}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\s/g, '').replace(/\D/g, '');
                                const formatted = value.match(/.{1,4}/g)?.join(' ') || value;
                                setPaymentData(prev => ({ ...prev, card_number: formatted }));
                              }}
                              placeholder="1234 5678 9012 3456"
                              maxLength={19}
                              className="h-11"
                            />
                          </div>
                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="card_expiry">Date d'expiration (MM/AA) *</Label>
                              <Input
                                id="card_expiry"
                                type="text"
                                value={paymentData.card_expiry}
                                onChange={(e) => {
                                  let value = e.target.value.replace(/\D/g, '');
                                  if (value.length >= 2) {
                                    value = value.slice(0, 2) + '/' + value.slice(2, 4);
                                  }
                                  setPaymentData(prev => ({ ...prev, card_expiry: value }));
                                }}
                                placeholder="12/25"
                                maxLength={5}
                                className="h-11"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="card_cvv">CVV *</Label>
                              <Input
                                id="card_cvv"
                                type="text"
                                value={paymentData.card_cvv}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '').slice(0, 3);
                                  setPaymentData(prev => ({ ...prev, card_cvv: value }));
                                }}
                                placeholder="123"
                                maxLength={3}
                                className="h-11"
                              />
                            </div>
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="card_holder_name">Nom sur la carte *</Label>
                            <Input
                              id="card_holder_name"
                              type="text"
                              value={paymentData.card_holder_name}
                              onChange={(e) => setPaymentData(prev => ({ ...prev, card_holder_name: e.target.value.toUpperCase() }))}
                              placeholder="JEAN DUPONT"
                              className="h-11"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Formulaire pour Orange Money */}
                    {paymentData.method_code === 'ORANGE_MONEY' && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-border">
                          <div className="w-8 h-8 bg-gradient-to-br from-orange-500 to-orange-700 rounded-lg flex items-center justify-center">
                            <Phone className="w-4 h-4 text-white" />
                          </div>
                          <span className="font-semibold">Orange Money</span>
                        </div>
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label htmlFor="orange_phone">Numéro de téléphone *</Label>
                            <div className="relative">
                              <div className="absolute left-3 top-1/2 -translate-y-1/2">
                                <Phone className="w-5 h-5 text-muted-foreground" />
                              </div>
                              <Input
                                id="orange_phone"
                                type="tel"
                                value={paymentData.phone_number}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                                  setPaymentData(prev => ({ ...prev, phone_number: value }));
                                }}
                                placeholder="6XX XXX XXX"
                                maxLength={9}
                                className="pl-10 h-11"
                              />
                            </div>
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Entrez votre numéro Orange Money (9 chiffres)
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Formulaire pour MTN Mobile Money */}
                    {paymentData.method_code === 'MTN_MOMO' && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-border">
                          <div className="w-8 h-8 bg-gradient-to-br from-yellow-500 to-yellow-700 rounded-lg flex items-center justify-center">
                            <Phone className="w-4 h-4 text-white" />
                          </div>
                          <span className="font-semibold">MTN Mobile Money</span>
                        </div>
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label htmlFor="mtn_phone">Numéro de téléphone *</Label>
                            <div className="relative">
                              <div className="absolute left-3 top-1/2 -translate-y-1/2">
                                <Phone className="w-5 h-5 text-muted-foreground" />
                              </div>
                              <Input
                                id="mtn_phone"
                                type="tel"
                                value={paymentData.phone_number}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                                  setPaymentData(prev => ({ ...prev, phone_number: value }));
                                }}
                                placeholder="6XX XXX XXX"
                                maxLength={9}
                                className="pl-10 h-11"
                              />
                            </div>
                            <p className="text-xs text-muted-foreground flex items-center gap-1">
                              <AlertCircle className="w-3 h-3" />
                              Entrez votre numéro MTN Mobile Money (9 chiffres)
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Formulaire pour Virement bancaire */}
                    {paymentData.method_code === 'BANK_TRANSFER' && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-border">
                          <Banknote className="w-5 h-5 text-primary" />
                          <span className="font-semibold">Virement bancaire</span>
                        </div>
                        <div className="space-y-3">
                          <div className="space-y-2">
                            <Label htmlFor="bank_name">Nom de la banque *</Label>
                            <Input
                              id="bank_name"
                              type="text"
                              value={paymentData.bank_name}
                              onChange={(e) => setPaymentData(prev => ({ ...prev, bank_name: e.target.value }))}
                              placeholder="Ex: BICIGUI, UBA, etc."
                              className="h-11"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="account_number">Numéro de compte *</Label>
                            <Input
                              id="account_number"
                              type="text"
                              value={paymentData.account_number}
                              onChange={(e) => setPaymentData(prev => ({ ...prev, account_number: e.target.value }))}
                              placeholder="Numéro de compte bancaire"
                              className="h-11"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="transfer_reference">Référence du virement *</Label>
                            <Input
                              id="transfer_reference"
                              type="text"
                              value={paymentData.reference}
                              onChange={(e) => setPaymentData(prev => ({ ...prev, reference: e.target.value }))}
                              placeholder="Référence de transaction"
                              className="h-11"
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Formulaire pour Paiement en espèces (CASH) */}
                    {paymentData.method_code === 'CASH' && (
                      <div className="space-y-4">
                        <div className="flex items-center gap-2 mb-4 pb-3 border-b border-border">
                          <DollarSign className="w-5 h-5 text-primary" />
                          <span className="font-semibold">Paiement en espèces</span>
                        </div>
                        <div className="space-y-4">
                          <div className="p-4 bg-muted/50 rounded-lg border border-border">
                            <div className="flex items-center gap-2 mb-2">
                              <AlertCircle className="w-4 h-4 text-primary" />
                              <span className="font-semibold text-sm">Preuve de paiement requise</span>
                            </div>
                            <p className="text-xs text-muted-foreground mb-3">
                              Téléchargez une photo ou un document PDF de la preuve de paiement (reçu, facture, etc.)
                            </p>
                            <div className="space-y-3">
                              <div className="relative">
                                <Input
                                  id="cash_proof"
                                  type="file"
                                  accept="image/*,.pdf"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      setPaymentData(prev => ({ ...prev, proof_file: file }));
                                    }
                                  }}
                                  className="cursor-pointer h-11"
                                />
                              </div>
                              {paymentData.proof_file && (
                                <div className="p-3 bg-background rounded-lg border border-border">
                                  <div className="flex items-center gap-3 mb-2">
                                    <FileText className="w-5 h-5 text-primary" />
                                    <div className="flex-1">
                                      <p className="text-sm font-medium">{paymentData.proof_file.name}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {(paymentData.proof_file.size / 1024).toFixed(2)} KB
                                      </p>
                                    </div>
                                    <Button
                                      type="button"
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => setPaymentData(prev => ({ ...prev, proof_file: null }))}
                                      className="h-8 w-8 p-0"
                                    >
                                      <X className="w-4 h-4" />
                                    </Button>
                                  </div>
                                  {paymentData.proof_file.type.startsWith('image/') && (
                                    <div className="mt-3">
                                      <img
                                        src={URL.createObjectURL(paymentData.proof_file)}
                                        alt="Aperçu"
                                        className="w-full max-w-md h-auto rounded-lg border border-border shadow-sm"
                                      />
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </Card>
                </div>
              )}
            </div>

            <DialogFooter className="pt-4 border-t border-border">
              <div className="flex gap-3 w-full">
                <Button
                  variant="outline"
                  onClick={() => {
                    setCreatePaymentDialogOpen(false);
                    setPaymentBooking(null);
                    setPaymentData({
                      method_code: '',
                      amount: '',
                      card_number: '',
                      card_expiry: '',
                      card_cvv: '',
                      card_holder_name: '',
                      phone_number: '',
                      account_number: '',
                      bank_name: '',
                      reference: '',
                      proof_file: null,
                    });
                  }}
                  className="flex-1"
                >
                  Annuler
                </Button>
                <Button
                  onClick={handleCreatePayment}
                  disabled={!paymentData.method_code || !paymentData.amount}
                  className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg"
                >
                  <CreditCard className="w-4 h-4 mr-2" />
                  {paymentData.method_code ? 'Payer maintenant' : 'Sélectionner une méthode'}
                </Button>
              </div>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </DashboardLayout>
    </>
  );
};

export default ClientOperations;
